// ════════════════════════════════════════════════════════════════════════════
//  ManDana — Settings
//  ----------------------------------------------------------------------------
//  INVENTED SURFACE. The prototype has no settings screen; R3 needs one because
//  location, calculation method and madhab all became real and all belong to the
//  user. Built from the existing vocabulary — <Glass>, the ink()/surf() tokens,
//  the compose sheet's takeover geometry and its 26px top radius — so it reads as
//  part of the app rather than bolted on, but it is NOT designed, and it is
//  expected to be replaced. Nothing in the existing UI was changed to add it.
//
//  Three sections:
//    Place       what we think it is, how confident we are, and three ways to
//                change it (precise fix, re-detect, pick a city)
//    Calculation the authority whose angles define Fajr and Isha
//    Asr         Shafi'i / Hanafi
//
//  Every change writes through immediately — there is no Save button, because
//  there is nothing here that is only valid as a set.
//
//  Exposes <SettingsSheet>.
// ════════════════════════════════════════════════════════════════════════════
const { useState: useSt, useEffect: useEt, useMemo: useMt, useRef: useRf } = React;
const { Glass: GlassS, ink: inkS, surf: surfS, mdRgba: rgbaS, mdIsLight: isLightS, MDI: MDIS } = window;

/* One row of a grouped list: label left, value/control right.
 *
 * `onRemove` HANGS BESIDE THE ROW, NOT INSIDE IT. The row is a <button>, and a
 * button inside a button is invalid HTML that every engine repairs in its own
 * way — usually by lifting the inner one out of the row, which is how the same
 * × ends up unclickable in one browser and renaming a device in another. So the
 * two controls become siblings in a flex wrapper, and the rule that separates
 * the rows moves to the wrapper with them: on the button it would have stopped
 * short of the × and drawn a visibly broken list.
 *
 * `reserveRemove` HOLDS THE GUTTER OPEN on a row that has no ×. Without it the
 * one row in the list that cannot be removed — the device in your hand — would
 * run 44px further right than every other, and the presence dots would step in
 * and out down the column. The gutter is spent by the list, once, or not at all.
 *
 * AND THE ORDER IS ×, THEN THE DOT (Marwan, 2026-08-27). So on a gutter row the
 * `right` content leaves the button and becomes the last sibling in the wrapper,
 * which is what puts the presence dot outboard of the ×. It stops being part of
 * the row's own tap target in the process, and that is correct: a dot is a fact,
 * not a control, and the row is pressed to rename it.
 */
/* ── the two ends of a row are not the same question ──────────────────────────
   ROUND TEN (Marwan, 2026-09-01): "Make the glyphs sit beautifully before the
   tab label instead of being at the opposite end of the tab."

   The LEFT of a row says what it is ABOUT — a place, a calendar, a key. The
   RIGHT says what pressing it DOES — a chevron opened a screen, a pencil an
   editor, a word is the answer already in force. Every mark on this surface
   used to stand at the right, so one column was answering both questions and
   neither was answered well: the sync arrows and the sign-out arrow are nouns
   about their row rather than directions, and they sat exactly where the
   chevrons pointed.

   ROUND ELEVEN, one line later: "now remove all the right side chevrons and
   glyphs from the root tabs". Once the left end says what the row is about,
   the right end is saying the obvious. A chevron means "this goes somewhere",
   and on a list where all but two rows go somewhere that is not information —
   it is one word repeated down a column, which is the thing the leading marks
   were brought in to stop. So the root's right end holds WORDS and nothing
   else: Dark and En, which are readings somebody actually wants. A screen
   keeps its chevrons, because inside Manage your data only Trash opens
   anything and there the mark is a difference rather than a refrain.

   `lead` is opt-in and the root is the only list that takes it. It is ALL OR
   NOTHING there — a half-filled column reads as unfinished in a way no single
   row does — and `lead={true}` reserves the column empty, which is what an
   answer opening in place under its own question needs so that it starts where
   that question's LABEL starts. A sub-screen takes none at all: a screen you
   have walked into is a list of choices, and a choice is not a subject.

   24 IS THE FACE'S MEASURE. See the comment at the column itself. */
const S_LEAD = 24;

/* THE COLUMN ITSELF, in one place. A row draws its lead inside a <button> and a
   storage reading draws one inside a <div>, and the two must land on the same
   pixel or the card has two left edges — which is exactly the defect this
   round's first pass shipped. `boxH` is the only thing that differs: a row
   centres its mark on the whole row, a reading centres it on its FIRST LINE,
   because everything under that line (a sub-sentence, a capacity track) belongs
   to the label and not to the mark. */
function SLead({ children, hidden, boxH }) {
  return (
    <span data-md-row-lead="" aria-hidden={hidden ? 'true' : undefined}
      style={{ flex: `0 0 ${S_LEAD}px`, width: S_LEAD, height: boxH || S_LEAD,
        color: inkS(0.72), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </span>);
}

function SRow({ label, labelColor, sub, right, rightPad, onClick, accent, last, onRemove, deviceName,
  removeLabel, removeAttr, reserveRemove, aside, lead }) {
  const light = isLightS();
  const rule = last ? 'none' : `1px solid ${inkS(light ? 0.07 : 0.06)}`;
  /* `aside` is the same gutter one step more general: a second control beside
     the row rather than inside it, because a button inside a button is invalid
     HTML and a screen reader reads the outer one. The × on a device proved the
     shape; the crosshair on Location is the second thing to want it, and a
     second bespoke path would have been the moment this became two shapes that
     drift. { glyph, label, onClick, attr, busy, accented } — the label is
     required and is the only name the control has.

     `accented` says the glyph arrives already coloured and is the screen's own
     verb rather than a quiet gutter control, so it keeps its colour instead of
     being dimmed at rest and lifted on hover. The location crosshair is the
     first: it is the door to finding you, and drawn in muted ink beside the
     reading it read as decoration. */
  const gutter = !!(onRemove || reserveRemove || aside);
  const rightInk = { fontSize: 14, fontWeight: 550, flexShrink: 0,
    display: 'flex', alignItems: 'center', gap: 6 };
  /* TWO KINDS OF WORD STAND AT THE RIGHT OF A ROW, and only one of them is
     something to do. `Dark`, `En` — a reading of what is already true, which
     you are looking at rather than reaching for. `Restore`, in the gutter
     beside a destructive ×, which is a press, and where the colour is doing
     the one job colour is for on a row with two outcomes: saying which of them
     is the safe one.

     Round eight took the accent off every glyph on the reasoning that an arrow
     saying "there is more here" is not a choice; round nine finishes the
     thought and takes it off the answers too (Marwan, 2026-09-01: "Make the
     Dark and En white, no need for accents there"). An answer in colour reads
     as unsaved — as something you have just changed — on a list where nothing
     has been changed at all. So the reading takes the marks' own ink, one
     fifth quieter than the question it answers, and the purple is left where a
     finger is genuinely being told something. */
  const answerInk = inkS(0.72);
  const verbInk = accent ? rgbaS(accent, 0.95) : inkS(0.5);
  const row = (
    <button onClick={onClick} disabled={!onClick}
    data-md-device-row={deviceName || undefined}
    style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
      background: 'transparent', border: 'none',
      padding: gutter ? '13px 0 13px 16px' : '13px 16px',
      cursor: onClick ? 'pointer' : 'default',
      borderBottom: gutter ? 'none' : rule }}>
      {/* THE LEADING COLUMN. Its width is the FACE's, not the glyphs' — a
          portrait is the widest thing that stands here, and a column sized to
          the marks would have made the one row with a picture in it the one
          row whose label does not line up. The glyphs are centred in it, so
          what the eye follows down the list is their centre line. */}
      {lead !== undefined && (
        <SLead hidden={lead === true}>{lead === true ? null : lead}</SLead>
      )}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', color: labelColor || inkS(0.92), fontSize: 15, fontWeight: 550 }}>{label}</span>
        {sub && <span style={{ display: 'block', color: inkS(0.45), fontSize: 12.5, marginTop: 2 }}>{sub}</span>}
      </span>
      {!gutter && right && (
        <span data-md-row-right="" style={{ ...rightInk, color: answerInk, paddingRight: rightPad || undefined }}>{right}</span>)}
    </button>);
  if (!gutter) return row;
  return (
    <div style={{ display: 'flex', alignItems: 'stretch', borderBottom: rule }}>
      {row}
      {/* Quiet, and no quieter than the caption it sits beside — there is no
          hover on a phone, so its resting weight is the only weight most people
          will ever see it at, and a control fainter than the words next to it
          reads as decoration. It comes up to full ink on hover and never appears
          in colour: a red × in a list of your own devices reads as an alarm
          about them. The touch target is the full height of the row. */}
      {aside ? (
        /* Same 40px column as the ×, so a list mixing the two keeps one edge.
           Quiet at rest and full ink on hover, for the reason written below. */
        /* A WORD IS ALLOWED IN THIS COLUMN, and it takes what it needs.
           40px is the glyph's measure — the × and the crosshair both sit in it
           — and a word given 40px is a word with three letters showing. So the
           width is a floor rather than a size, exactly as it already is for the
           `right` column two comments below. A worded aside is the screen's
           own verb rather than a quiet gutter control, so it wears the accent
           and skips the ink-on-hover the glyphs do. */
        <button type="button" onClick={aside.busy ? null : aside.onClick} aria-label={aside.label}
          disabled={!!aside.busy} {...(aside.attr || {})}
          onMouseEnter={aside.text || aside.accented ? undefined
            : (e) => { e.currentTarget.style.color = inkS(0.9); }}
          onMouseLeave={aside.text || aside.accented ? undefined
            : (e) => { e.currentTarget.style.color = inkS(0.5); }}
          style={{ flexShrink: 0, minWidth: 40, display: 'flex', alignItems: 'center',
            justifyContent: 'center', background: 'transparent', border: 'none',
            whiteSpace: 'nowrap',
            cursor: aside.busy ? 'default' : 'pointer',
            color: aside.text ? rgbaS(accent, 0.95) : inkS(0.5),
            fontSize: aside.text ? 13.5 : undefined, fontWeight: aside.text ? 600 : undefined,
            /* The glyph column is 40px of gutter; a verb wearing the accent
               takes the row's own 16px edge instead, so it lines up with the
               `right` column every other row on this surface uses. */
            padding: aside.text ? '0 14px' : (aside.accented ? '0 16px 0 8px' : 0) }}>
          {aside.busy ? '…' : (aside.text || aside.glyph)}
        </button>
      ) : onRemove ? (
        <button type="button" onClick={onRemove} aria-label={removeLabel || `Remove ${deviceName}`}
          data-md-device-remove={deviceName || undefined} {...(removeAttr || {})}
          onMouseEnter={(e) => { e.currentTarget.style.color = inkS(0.9); }}
          onMouseLeave={(e) => { e.currentTarget.style.color = inkS(0.5); }}
          style={{ flexShrink: 0, width: 40, background: 'transparent', border: 'none',
            cursor: 'pointer', color: inkS(0.5), fontSize: 18, lineHeight: 1,
            fontWeight: 400, padding: 0 }}>
          ×
        </button>
      ) : (
        /* The held gutter. aria-hidden and unfocusable: it is alignment, and a
           screen reader announcing an empty cell on every row would be worse
           than the ragged edge it exists to prevent. */
        <span aria-hidden="true" style={{ flexShrink: 0, width: 40 }} />
      )}
      {/* Outboard of the ×, and holding its width whether or not there is a dot
          to draw — otherwise the ×s themselves would step in and out down the
          column, which is the same raggedness one column further left. The 16px
          is the row's own right padding, spent here now that the row has none. */}
      {/* A FLOOR, NOT A WIDTH. The 23 was measured for the dot — 7px of it and
          16px of the row's own right padding — and it holds the ×s in one line
          down a list where most rows have no dot to draw. Trash is the first
          screen to put a WORD here, and a word given the dot's column is a word
          with three letters showing. So the column is reserved and not
          imposed: a dot still takes exactly 23px and the devices list is
          untouched, a word takes what it needs, and nothing here wraps —
          "Restore" broken over two lines is the same defect one step later. */}
      {/* AND IT IS NOT RESERVED WHERE THERE IS NOTHING TO KEEP IN LINE. A row
          carrying an `aside` and no `right` of its own — the location
          crosshair is the only one — was pushed 23px in from the edge a
          row's right column sits at, holding a column open for a dot that
          list has no rows to draw. The reservation is for a LIST that mixes
          rows with and without a dot; a lone verb is not that list. */}
      {(right || !aside) && (
        <span style={{ ...rightInk, color: verbInk, boxSizing: 'border-box', minWidth: 23,
          paddingRight: 16, whiteSpace: 'nowrap', justifyContent: 'flex-start' }}>{right}</span>
      )}
    </div>);
}

/* THE SOON MARK, and the first thing in this app to make a promise about the
   future on a screen rather than in a document.

   It is deliberately plain: no colour of its own, no glow, nothing that reads
   as new-and-exciting. A row wearing this is a row that does nothing, and the
   mark's whole job is to say so before the finger arrives — the alternative
/* A device that is in the room right now. STATES A FACT, DOES NOT CELEBRATE
   ONE — a second device arriving is not an achievement — so it is a dot the
   size of a full stop, no animation and no count. Taste rather than law: the
   app-wide rule forbids shame, and says nothing about applause. It carries a
   name for assistive technology
   because it is the only thing in the row saying this, and it is not a control:
   there is nothing to press on a fact. */
function LiveMark({ accent, self }) {
  return (
    <span role="img" aria-label={self ? 'This device, here now' : 'Here now'}
      data-md-device-live=""
      style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 4,
        background: rgbaS(accent, self ? 0.55 : 0.95) }} />);
}

/* ── the code that adds a device ─────────────────────────────────────────────
   Two ways to carry the same ten seconds of work across to the other screen,
   side by side and neither of them the "real" one: point the other device's
   camera at the picture, or read the code out and type it.

   THE PICTURE IS A LINK. `https://<this origin>/?link=CODE` — so the phone's
   own camera app is the scanner: it sees a link, offers to open it, and the app
   comes up already holding the code. That is why the origin is read from
   `location` rather than written down anywhere: a code minted on staging must
   open staging, whose database is the only one that has heard of it.

   AND THE CODE IS ALWAYS THERE. A camera can be broken, refused, or on a laptop
   that has none, and the encoder itself can decline. The letters underneath are
   the floor, which is why they are drawn at a size somebody can actually read
   aloud rather than as a caption. */
function DeviceLinkCode({ code, light }) {
  const Qr = window.MdQrCode;
  const url = window.mdLinkUrl ? window.mdLinkUrl(window.location.origin, code) : '';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 16px',
      borderBottom: `1px solid ${inkS(light ? 0.07 : 0.06)}`, background: inkS(0.03) }}>
      {Qr && url && <Qr text={url} size={116} label="Scan this code on the other device" />}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', color: inkS(0.92), fontSize: 15, fontWeight: 600,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', letterSpacing: '0.04em',
          wordBreak: 'break-all', userSelect: 'text' }}>{code}</span>
        <span style={{ display: 'block', color: inkS(0.45), fontSize: 12.5, marginTop: 6 }}>
          Valid 10 minutes, once.
        </span>
      </span>
    </div>);
}

/* ── the question the × asks ─────────────────────────────────────────────────
   Removal is not reversible from this screen, so it is asked rather than done.
   The grammar is app/md-pair.jsx's, deliberately: this app has one shape for a
   confirmation over a surface — a portal to <body> on the `confirm` plane, the
   shared dismiss layer, a primary and a quiet Cancel — and a second shape here
   would be a second set of manners to keep true.

   IT NAMES WHAT HAPPENS, AND WHAT DOES NOT. "Remove" alone says nothing about
   the device: it is signed out, and its passkey travels with the person's
   account, so it can sign in again whenever they want it to. Both halves are
   said in the open. A dialog that let somebody believe removal was permanent
   would be the more dangerous of the two misunderstandings — it is the one that
   ends with a sold phone assumed to be locked out.

   NOTHING TAKES FOCUS ON MOUNT, for md-pair.jsx's reason: there is no field to
   fill here, and putting the caret on the destructive button would leave it
   under whatever the next keypress happened to be. */
/* Each one names what did NOT happen, because the row is pressed by somebody
   who cannot see whether it worked and whose next move depends on the answer.
   `already` is the odd one out and deliberately not phrased as a refusal: the
   authenticator declining to mint a duplicate is this device reporting that it
   is set up, which is the news the person wanted. */
const MD_ADD_PASSKEY_NOTE = {
  already: 'This device already has a passkey for your account.',
  cancelled: 'No passkey was added.',
  unsupported: 'This browser cannot hold a passkey.',
  unreachable: 'Could not reach the server, so nothing was added.',
  options_failed: 'Could not start, so nothing was added.',
  create_failed: 'This device could not make a passkey.',
  verify_failed: 'The server did not accept it, so nothing was added.',
  failed: 'That did not go through, and nothing was added.',
};

/* Every one of these ends in "and nothing was signed out", because the harm this
   row can do is not failing — it is a person walking away believing their other
   screens are closed when they are still open. */
const MD_SIGN_OUT_OTHERS_NOTE = {
  unreachable: 'Could not reach the server, so nothing was signed out.',
  not_signed_in: 'You are not signed in any more, so nothing was signed out.',
  failed: 'That did not go through, and nothing was signed out.',
};

const MD_DEVICE_REMOVE_REFUSAL = {
  unreachable: 'Could not reach the server, so nothing was removed. '
    + 'This device is still signed in.',
  not_signed_in: 'You are not signed in any more, so nothing was removed.',
  bad_device: 'That device could not be identified, and nothing was removed.',
  malformed: 'The server answered something this app could not read. Nothing was removed.',
  failed: 'That did not go through, and nothing was removed.',
};

/* ONE ASK, WORN BY BOTH OF THEM. Two dialogs in this file interrupt a person to
   check they meant it, and they are the same object in every respect that is not
   words: the plane, the dismiss layer, the glass, the two buttons and which of
   them is loud. Written twice they would be two that drift — the codebase has
   the scar (app/md-gate.ts, four teardowns that were nearly the same).

   `attr` is the one thing a caller must give beyond words: the data attribute a
   test finds this dialog by, so each ask stays addressable as itself.

   `cancel` renames the quiet button, and null REMOVES it.

   `onDismiss` separates Escape-and-outside from the quiet button, for the one
   dialog whose quiet button DOES something (the walkthrough's Choose a city):
   a person backing out of a question must not be handed the answer they were
   backing out of. Left unset, the two exits stay one, as everywhere else. */
/* THE ASK STANDS ABOVE THE KEYBOARD. `inset: 0` is the LAYOUT viewport, and
   on a phone or a tablet a focused field raises the keyboard INSIDE that box —
   a centred card can sit exactly under the keys, unreachable. So the overlay
   pins itself to the visual viewport, the box the person can actually see,
   and follows its resize and scroll — the shape the landing panel already
   proved (mdLandingKeyboardBox, md-landing.jsx). The math is a local twin
   rather than a borrow: these classic scripts load in their own order, and
   this sheet is proved without the landing in the room. */
function mdAskRoomS(vv) {
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const height = vv && Number(vv.height);
  const width = vv && Number(vv.width);
  const across = width && Number.isFinite(width)
    ? { left: num(vv.offsetLeft), width } : { left: 0, width: '100%' };
  if (!height || !Number.isFinite(height)) return { top: 0, height: '100%', ...across };
  return { top: num(vv.offsetTop), height, ...across };
}

function AskS({ open, attr, label, title, note, busy, held, confirm, working,
  onConfirm, onCancel, onDismiss, accent, cancel, pinned, children }) {
  const light = isLightS();
  window.useMdDismissLayer(() => { if (!busy) (onDismiss || onCancel)(); }, !!open, null, 'confirm');
  const [room, setRoom] = useSt(() => mdAskRoomS(window.visualViewport));
  useEt(() => {
    if (!open) return undefined;
    const vv = window.visualViewport;
    /* The ask stays mounted between opens, so the last box it measured — a
       keyboard-shrunk one, say — is what it would reopen with. Re-measure at
       every open, and with no viewport to ask, stand down to the whole screen. */
    if (!vv) { setRoom(mdAskRoomS(null)); return undefined; }
    const place = () => setRoom(mdAskRoomS(window.visualViewport));
    place();
    vv.addEventListener('resize', place);
    vv.addEventListener('scroll', place);
    return () => {
      vv.removeEventListener('resize', place);
      vv.removeEventListener('scroll', place);
    };
  }, [open]);
  if (!open) return null;

  const plane = window.mdSceneRegistry?.().confirm?.plane ?? window.MD_SHEET_ASK_Z ?? 200;
  /* `held` dims the primary the way busy does but keeps its own word: the
     door is not open YET, and the label is what will open it. It never blocks
     the dismissal — a requirement is not a trap. */
  const stalled = busy || held;
  const primary = { width: '100%', marginTop: 18, padding: '12px 14px', borderRadius: 12,
    cursor: stalled ? 'default' : 'pointer', border: 0,
    background: stalled ? inkS(0.1) : rgbaS(accent, 1), color: stalled ? inkS(0.32) : '#fff',
    fontSize: 14.5, fontWeight: 700, letterSpacing: '.01em' };
  const quiet = { width: '100%', marginTop: 9, padding: '10px', borderRadius: 11,
    cursor: busy ? 'default' : 'pointer', background: 'transparent',
    border: `1px solid ${inkS(0.14)}`, color: inkS(0.66), fontSize: 13.5, fontWeight: 600 };

  return ReactDOM.createPortal(
    <div data-md-ask-room="" style={{ position: 'fixed', top: room.top, left: room.left,
      width: room.width, height: room.height, zIndex: plane, display: 'flex',
      containerType: 'size',
      justifyContent: 'center', overflowY: 'auto', padding: 24,
      background: light ? 'rgba(239,241,244,0.9)' : 'rgba(4,6,12,0.84)',
      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
      animation: 'mdConfIn .2s ease' }}>
      {/* margin:auto centres the card while it fits and yields to the scroll
          when it cannot — alignItems:center clips a tall card's top clean off.
          A PINNED card hangs from the top instead: a centred card re-centres
          every time its content changes height, and under a picker that is a
          dialog jumping with every keystroke (Marwan, 2026-08-31). */}
      <GlassS radius={22} alpha={0.12} style={{ padding: 22, width: 'min(420px, 100%)',
        margin: pinned ? '24px auto auto' : 'auto', flex: '0 0 auto',
        animation: 'mdConfPop .26s cubic-bezier(.22,1,.36,1)' }}>
        <div {...attr} role="dialog" aria-modal="true" aria-label={label}>
          <div style={{ color: inkS(0.95), fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
            {title}
          </div>
          {children}

          {note && <p role="status" style={{ margin: '14px 0 0',
            color: inkS(0.72), fontSize: 12.5, lineHeight: 1.55 }}>
            {note}
          </p>}

          <button type="button" style={primary} disabled={stalled}
            onClick={() => { if (!busy && !held) onConfirm(); }}>
            {busy ? working : confirm}
          </button>
          {cancel !== null && (
            <button type="button" style={quiet} disabled={busy} onClick={onCancel}>
              {cancel || 'Cancel'}
            </button>
          )}
        </div>
      </GlassS>
    </div>,
    document.body,
  );
}

/* The two paragraphs every ask in this file is built from: what happens, then
   the quieter line about what does NOT.

   FUNCTIONS, NOT OBJECTS, and that is the whole of what this comment is for.
   `ink()` answers for the theme in force AT THE MOMENT IT IS ASKED. Held as two
   module-level constants these were asked once, at load, when the theme happened
   to be dark — and every word of both dialogs then rendered white on white for
   anybody using the app in light. It shipped to staging and no test saw it:
   textContent cannot read a colour. Anything that lifts `ink()` out of render is
   answering for ever on behalf of whoever was looking first. */
const askSaid = () => ({ margin: 0, color: inkS(0.72), fontSize: 13.5, lineHeight: 1.6 });
const askAside = () => ({ margin: '12px 0 0', color: inkS(0.5), fontSize: 12.5, lineHeight: 1.6 });

function DeviceRemoveAsk({ device, note, busy, onConfirm, onCancel, accent }) {
  return (
    <AskS open={!!device} accent={accent} busy={busy}
      attr={device ? { 'data-md-device-remove-ask': device.id } : null}
      label={device ? `Remove ${device.name}` : ''}
      title={device ? `Remove ${device.name}?` : ''}
      confirm="Remove" working="Removing…"
      note={note ? (MD_DEVICE_REMOVE_REFUSAL[note] || MD_DEVICE_REMOVE_REFUSAL.failed) : null}
      onConfirm={onConfirm} onCancel={onCancel}>
      <p style={askSaid()}>
        It is signed out and leaves this list. Nothing on it is deleted, and
        nothing of yours is lost — everything stays in your account.
      </p>
      <p style={askAside()}>
        Your passkey travels with your account, so a device you still hold can
        sign in again. This ends what it can reach now — it is not a lock.
      </p>
    </AskS>
  );
}

/* THE ONE THAT REACHES WHAT NO × CAN. The × above needs a device to name, so it
   can only end a session that stamped itself; this ends everything on the
   account except the screen asking, named or not. Same two facts, in the same
   order: what goes, and then that it is a lock rather than a revocation. */
function SignOutOthersAsk({ open, note, busy, onConfirm, onCancel, accent }) {
  return (
    <AskS open={open} accent={accent} busy={busy} note={note}
      attr={{ 'data-md-sign-out-others-ask': '' }}
      label="Sign out everywhere else" title="Sign out everywhere else?"
      confirm="Sign out the others" working="Signing out…"
      onConfirm={onConfirm} onCancel={onCancel}>
      <p style={askSaid()}>
        Every other screen signed in to your account is signed out. This one
        stays. Nothing is deleted, and nothing of yours is lost.
      </p>
      <p style={askAside()}>
        Your passkey travels with your account, so any device you still hold can
        sign in again. This ends what they can reach now — it is not a lock.
      </p>
    </AskS>
  );
}

function SSection({ label, children }) {
  return (
    <div style={{ marginTop: 22 }}>
      <div className="md-label" style={{ color: inkS(0.4), fontSize: 11, fontWeight: 400,
        padding: '0 4px 8px' }}>{label}</div>
      <GlassS radius={18} alpha={0.08} style={{ overflow: 'hidden' }}>{children}</GlassS>
    </div>);
}

/* ── Trash, in words first ──────────────────────────────────────────────────
   An entry is one deletion and its members are everything that moved with it.
   The root gets the name; the rest get counted in the words people already see
   elsewhere — desks, projects, records — rather than their storage kinds.

   PURE AND CLOCKED. A Trash list left open across midnight has to say a
   different number without changing the entry, and a sentence tested through
   computed layout would say nothing in happy-dom. */
const MD_TRASH_DAY_MS_S = 24 * 60 * 60 * 1000;
const MD_TRASH_KIND_S = [
  { types: ['pursuit'], one: 'pursuit', many: 'pursuits' },
  { types: ['project'], one: 'project', many: 'projects' },
  { types: ['board'], one: 'desk', many: 'desks' },
  { types: ['database'], one: 'database', many: 'databases' },
  { types: ['craft', 'manifest'], one: 'Craft', many: 'Crafts' },
  { types: null, one: 'record', many: 'records' },
];

const mdTrashJoinS = (parts) => parts.length > 1
  ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
  : (parts[0] || '');

function mdTrashRowSay(entry, now = Date.now()) {
  const e = entry && typeof entry === 'object' ? entry : {};
  const members = Array.isArray(e.members) ? e.members.filter((m) => m && typeof m === 'object') : [];
  const root = members.find((m) => m.ref === e.ref) || members[0] || {};
  const name = typeof root.title === 'string' && root.title.trim() ? root.title.trim() : 'Deleted item';
  const counts = new Map();
  for (const member of members) {
    if (member === root) continue;
    const type = typeof member.ref === 'string' ? member.ref.split(':')[0] : '';
    const kind = MD_TRASH_KIND_S.find((row) => !row.types || row.types.includes(type));
    counts.set(kind, (counts.get(kind) || 0) + 1);
  }
  const parts = MD_TRASH_KIND_S.flatMap((kind) => {
    const n = counts.get(kind) || 0;
    return n ? [`${n} ${n === 1 ? kind.one : kind.many}`] : [];
  });
  const came = parts.length ? `${mdTrashJoinS(parts)} came with it` : 'Nothing else came with it';
  const deletedAt = Number(e.deletedAt);
  const clock = typeof now === 'number' && Number.isFinite(now) ? now : Date.now();
  const daysLeft = Number.isFinite(deletedAt)
    ? Math.max(0, Math.ceil((30 * MD_TRASH_DAY_MS_S - Math.max(0, clock - deletedAt)) / MD_TRASH_DAY_MS_S))
    : 0;
  const days = `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`;
  return { name, came, daysLeft, detail: `${came} · ${days}` };
}

/** The confirmation around one row action. Kept pure so punctuation and the
    irreversible boundary are a product decision with a test, not JSX glue. */
function mdTrashAskSay(row, action) {
  const r = row && typeof row === 'object' ? row : mdTrashRowSay(null);
  if (action === 'purge') return {
    label: `Delete ${r.name} now`, title: `Delete “${r.name}” now?`,
    body: r.came === 'Nothing else came with it'
      ? `“${r.name}” leaves Trash now. This cannot be undone.`
      : `“${r.name}” and the ${r.came.replace(' came with it', '')} that came with it leave Trash now. This cannot be undone.`,
    confirm: 'Delete now', working: 'Deleting…',
  };
  return {
    label: `Restore ${r.name}`, title: `Restore “${r.name}”?`,
    body: r.came === 'Nothing else came with it'
      ? `“${r.name}” returns. Nothing needs to be uploaded again.`
      : `“${r.name}” returns with ${r.came.replace(' came with it', '')}. Nothing needs to be uploaded again.`,
    confirm: 'Restore', working: 'Restoring…',
  };
}

function TrashAskS({ ask, busy, note, accent, onConfirm, onCancel }) {
  const said = ask ? mdTrashAskSay(ask.row, ask.action) : mdTrashAskSay(null, 'restore');
  return (
    <AskS open={!!ask} accent={accent} busy={busy} note={note}
      attr={ask ? { 'data-md-trash-ask': ask.action } : null}
      label={said.label} title={said.title} confirm={said.confirm} working={said.working}
      onConfirm={onConfirm} onCancel={onCancel}>
      <p style={askSaid()}>{said.body}</p>
    </AskS>
  );
}

/* The thin half: SSection and SRow draw answers from the two pure functions;
   AskS owns every irreversible press. The × is a sibling of the row, through
   SRow's existing split-button shape, so no browser has to repair a button
   nested inside another button. */
function TrashViewS({ accent }) {
  const [ask, setAsk] = useSt(null);
  const [busy, setBusy] = useSt(false);
  const [note, setNote] = useSt(null);
  const [changed, setChanged] = useSt(0);
  const entries = typeof window.mdTrashList === 'function' ? window.mdTrashList() : [];
  const rows = (Array.isArray(entries) ? entries : []).map((entry) => ({ entry, ...mdTrashRowSay(entry) }));

  const act = async () => {
    if (!ask || busy) return;
    setBusy(true); setNote(null);
    const fn = ask.action === 'purge' ? window.mdTrashPurge : window.mdTrashRestore;
    let res = null;
    try { res = typeof fn === 'function' ? await fn(ask.entry.ref) : null; } catch (e) { res = null; }
    setBusy(false);
    if (!res || !res.ok) {
      setNote(ask.action === 'purge'
        ? 'That item is still in Trash.' : 'That item could not be restored.');
      return;
    }
    const finished = ask;
    setAsk(null);
    setChanged((n) => n + 1);
    if (finished.action === 'restore') setNote(`“${finished.row.name}” is back.`);
    else {
      const held = window.mdDeleteSay ? window.mdDeleteSay({ kept: res.kept }).held : '';
      setNote(held || `“${finished.row.name}” was deleted now.`);
    }
  };
  void changed;

  return (
    <>
      <TrashAskS ask={ask} busy={busy} note={ask ? note : null} accent={accent}
        onConfirm={act} onCancel={() => { setAsk(null); setNote(null); }} />
      <p style={{ color: inkS(0.5), fontSize: 12.5, lineHeight: 1.55, margin: '0 4px 12px' }}>
        Deleted items stay here for 30 days. Restore one, or delete it now.
      </p>
      {note && !ask && <p role="status" style={{ color: inkS(0.66), fontSize: 12.5,
        lineHeight: 1.5, margin: '0 4px 10px' }}>{note}</p>}
      {!rows.length ? (
        <p style={{ color: inkS(0.48), fontSize: 13.5, margin: '22px 4px' }}>Trash is empty.</p>
      ) : (
        <SSection label="Deleted">
          {rows.map((row, i) => (
            <SRow key={row.entry.ref} label={row.name} sub={row.detail} right="Restore"
              accent={accent} last={i === rows.length - 1}
              onClick={() => { setNote(null); setAsk({ action: 'restore', entry: row.entry, row }); }}
              onRemove={() => { setNote(null); setAsk({ action: 'purge', entry: row.entry, row }); }}
              removeLabel={`Delete ${row.name} now`}
              removeAttr={{ 'data-md-trash-delete-now': row.entry.ref }} />
          ))}
        </SSection>
      )}
    </>
  );
}

/* ── how much room is left ───────────────────────────────────────────────────
   "Your data" used to be one row whose subtitle was a `·`-joined sentence:
   the persistence grant, two different measures of fullness and the last write
   that failed, run together in a caption. Four facts of four kinds, and none of
   them answering the question a person actually has, which is "am I safe, and
   how much room is left?".

   THE BROWSER BAR IS MEASURED AGAINST THE KEY WALL, NOT navigator.storage
   .estimate(). md-storage.js:840-852 has the full reason and it is worth the
   line here too: the estimate reports the ORIGIN'S whole quota — gigabytes,
   nearly all of it IndexedDB — and cannot see localStorage's own fixed ceiling,
   which is the one an ordinary day of writing can actually hit. On 2026-08-14
   the estimate read 4.3 MB of 36 GB on a device that was throwing
   QuotaExceededError on every board save. A bar drawn from that number would
   have been an empty bar on a full device, which is worse than no bar.

   About 5 MB, per origin: the Web Storage spec suggested it as "mostly
   arbitrary" and every engine froze it in place. It is not exact and it is not
   ours, so the words beside it say "of 5 MB" rather than promising a limit the
   browser never published. */
const MD_KEY_WALL_BYTES = 5 * 1024 * 1024;

/** Bytes as a person would say them. Trailing `.0` is dropped, because "10.0 GB"
    is a machine talking. */
function mdBytesSaid(n) {
  if (typeof n !== 'number' || !Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${Math.round(n)} B`;
  const step = n < 1048576 ? ['KB', 1024] : n < 1073741824 ? ['MB', 1048576] : ['GB', 1073741824];
  const value = n / step[1];
  const said = value < 10 ? value.toFixed(1) : String(Math.round(value));
  return `${said.replace(/\.0$/, '')} ${step[0]}`;
}

/* The Librarian's own four words, least settled first (md-librarian.ts:304-309).
   A KIND IS REPORTED AT ITS LEAST SETTLED MEMBER: two desks, one of which has
   never left this device, is not a pair that may be called Synced. The same
   rule the Librarian applies across a record's own documents, applied one level
   up across the records of a kind. */
const MD_WHERE_ORDER = ['This device only', 'Changed since sync', 'Synced', 'Built in'];

/** What a shelf of many documents says about itself, in one sentence.

    A WORST-OF READING NEEDS A COUNT OR IT IS NOT A READING. On 2026-08-31 this
    row said "823 records · Changed since sync" and went on saying it across a
    successful sync and a full page reload, while Sync now reported nothing
    left to send. Both halves were true: ONE document was unsettled, and the
    row had no way to say "one". It is the second worst-of reduction in a row —
    md-librarian.ts:410 already takes the worst across a record's own documents
    — and stacking them without a number turns a single stuck document into a
    whole shelf of unsaved work.

    So the worst word STAYS, because it is the one that matters and burying it
    in an average is the failure this reduction exists to avoid. What is added
    is the arithmetic: how many are settled, and how many are not.

    A SHELF THAT AGREES SAYS THE PLAIN WORD. "823 synced" in front of "Synced"
    is noise; a count answers a disagreement and there is none. Pure over an
    array of the Librarian's words, so it is provable without a store. */
function mdShelfSaid(states, fallback) {
  let best = null;
  const seen = new Map();
  for (const state of (states || [])) {
    const rank = MD_WHERE_ORDER.indexOf(state);
    if (rank < 0) continue;                              // a word this build does not know
    seen.set(rank, (seen.get(rank) || 0) + 1);
    if (best === null || rank < best) best = rank;
  }
  if (best === null) return fallback;
  const worst = MD_WHERE_ORDER[best];
  if (seen.size < 2) return worst;
  /* Settled first and unsettled after it, each named and counted, most settled
     to least — the same ordering as the words themselves, read the other way
     so the reassuring half leads and the thing to act on ends the sentence.

     THE WORDS ARE THE LIBRARIAN'S, UNCHANGED, capital and all. They are a
     vocabulary this app shares across three surfaces (tests/where-says.test.js
     pins them), and a count is not a licence to start spelling them a second
     way. */
  const parts = [...seen.keys()].sort((a, b) => b - a)
    .map((rank) => `${seen.get(rank)} ${MD_WHERE_ORDER[rank]}`);
  return parts.join(' · ');
}

/* A capacity bar, and there is deliberately no indeterminate state.
   `prefers-reduced-motion` is a blanket rule in index.html, so a shimmer would
   be a moving thing on a screen that asked for none; and a bar that animates
   while it waits says "working" when the honest answer, until the store
   answers, is that we do not know yet. Until then there is no bar at all.

   role="progressbar" with all three values, because a bar is a picture of a
   number and a screen reader gets the number rather than the picture. */
/* `lead` IS THE LEADING COLUMN, REACHING PAST THE ROWS. On the root every row
   carries a mark, and a reading flush against the card's own 16 underneath them
   gives the card two text edges — the raggedness round eight spent itself on,
   one column further right. So the two readings stand in the same column and
   say the same kind of thing the marks above them say: WHICH STORE this is a
   number for. The first pass faked the edge with padding; a mark makes the
   column real, and there is one mechanism instead of two.

   Inside Manage your data nothing is passed, so nothing is drawn — a screen you
   walked into is a list of choices and a choice is not a subject. The rule under
   each block still runs the card's full width: it separates rows, and it is not
   text. */
function CapacityBarS({ which, label, used, total, accent, last, lead }) {
  const light = isLightS();
  const share = total > 0 ? Math.min(100, Math.max(0, (used / total) * 100)) : 0;
  /* Rounded to a tenth of a percent before it becomes a width. 2 GB of 10 GB is
     0.2, and 0.2 × 100 is not 20 in binary floating point — the raw number
     would ship a style attribute reading "20.000000000000004%".

     AND A FLOOR UNDER THE ROUNDING, because a tenth of a percent is not small
     enough. 1.6 KB of the 5 MB wall is 0.03%, which rounds to nought, and an
     empty track is the picture a bar draws when it FAILED to measure. The two
     answers a glance must never confuse are "a little" and "none at all", so
     anything held draws something. The floor is only ever reached from above:
     an empty store still draws 0%, because a sliver over nothing would be the
     same lie with the sides swapped. */
  const drawn = Math.round(share * 10) / 10;
  const width = `${drawn || (share > 0 ? 0.4 : 0)}%`;
  const said = `${mdBytesSaid(used)} of ${mdBytesSaid(total)}`;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: lead ? 12 : 0,
      padding: '12px 16px',
      borderBottom: last ? 'none' : `1px solid ${inkS(light ? 0.07 : 0.06)}` }}>
      {lead && <SLead boxH={20}>{lead}</SLead>}
      <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <span style={{ flex: 1, minWidth: 0, color: inkS(0.92), fontSize: 15, fontWeight: 550 }}>
          {label}
        </span>
        {/* Selectable, like every number here somebody might want to quote back
            to us: the app switches selection off everywhere and `.md-live` is
            how a run of text asks for it back (index.html:339). */}
        <span className="md-live" style={{ color: inkS(0.5), fontSize: 13,
          fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{said}</span>
      </div>
      <div role="progressbar" data-md-capacity={which} aria-label={label}
        aria-valuemin={0} aria-valuemax={total} aria-valuenow={used} aria-valuetext={said}
        style={{ marginTop: 9, height: 6, borderRadius: 3, overflow: 'hidden',
          background: inkS(light ? 0.09 : 0.10) }}>
        <div aria-hidden="true" style={{ width, height: '100%', borderRadius: 3,
          background: rgbaS(accent, share >= 90 ? 1 : 0.75) }} />
      </div>
      </div>
    </div>);
}

/* One kind of thing, how many there are, how much room they take and where they
   are. NOT a control: there is nothing to press on a fact, and an SRow would
   promise otherwise. */
function DataLineS({ kind, label, count, noun, nouns, where, bytes, last }) {
  const light = isLightS();
  const live = { fontVariantNumeric: 'tabular-nums' };
  return (
    <div data-md-data-kind={kind}
      style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '10px 16px',
        borderBottom: last ? 'none' : `1px solid ${inkS(light ? 0.07 : 0.06)}` }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', color: inkS(0.86), fontSize: 14, fontWeight: 500 }}>{label}</span>
        <span style={{ display: 'block', color: inkS(0.42), fontSize: 12, marginTop: 2 }}>
          {count != null && (
            <>
              <span className="md-live" style={live}>{count}</span>
              {` ${count === 1 ? noun : nouns} · `}
            </>
          )}
          {where}
        </span>
      </span>
      {bytes != null && (
        <span className="md-live" style={{ ...live, color: inkS(0.55), fontSize: 13, flexShrink: 0 }}>
          {mdBytesSaid(bytes)}
        </span>
      )}
    </div>);
}

/* ── the widget ──────────────────────────────────────────────────────────────
   Two bars, a breakdown, every transfer, the persistence grant and the last
   write that failed.

   THE CLOUD NUMBER COMES THROUGH THE SEAM AND NOWHERE ELSE. `blobUsage()` on a
   transport this file never names — `mdTransportName()` answers which one, the
   way md-person.ts:295 and md-live.ts already do. A `fetch('/api/blob/usage')`
   here would work perfectly today and would be the day a Blossom or a mesh
   transport is registered spent in a hunt; tests/seam.test.js fails the source
   that writes one.

   `allowance: null` MEANS THIS STORE STATES NONE (md-transport.ts:30) — the
   ordinary answer for a total summed from a list. It degrades to a plain total,
   never to a bar, because a bar needs a denominator and every denominator
   available here would be a client constant impersonating a server's policy.

   AND A REFUSAL IS NOT A ZERO. "I could not ask" and "you are storing nothing"
   are different sentences and only one of them is safe to put in front of
   somebody. */
/* THE READING, ASKED ONCE AND IN ONE PLACE. Two screens draw from it now — the
   root's two bars, for the glance that answers "am I running out of room?", and
   the full widget inside Manage your data. They are on different frames, so only
   ever one of them is mounted and nothing is fetched twice; what this hook
   prevents is not a double fetch but a second copy of the four questions, which
   is how two screens start disagreeing about the same number. */
function useStorageReadingS() {
  const [persisted, setPersisted] = useSt(undefined);   // undefined until asked
  const [cloud, setCloud] = useSt(null);
  const [localBytes, setLocalBytes] = useSt(null);
  const [kinds, setKinds] = useSt(null);
  useEt(() => {
    let alive = true;
    (async () => {
      const [grant, usage] = await Promise.all([askPersistedS(), askCloudUsageS()]);
      const shelves = await askKindsS();
      if (!alive) return;
      setPersisted(grant);
      setCloud(usage);
      setKinds(shelves);
      setLocalBytes(window.mdStorageLocalBytes ? window.mdStorageLocalBytes() : null);
    })();
    return () => { alive = false; };
  }, []);

  /* AND AGAIN WHEN A SYNC ENDS. This is main's fix (2026-09-01) moved to where
     the state it writes actually lives: it arrived as an effect inside
     StorageWidget, which is where this reading used to be held, and S-10 had
     since lifted the four questions into this hook. The merge was textually
     clean and semantically broken — the effect kept calling `setKinds` and
     `setLocalBytes`, which are no longer in that component's scope, so opening
     Manage your data threw. A clean merge is not an argument.

     The law is main's, unchanged: this panel reports on a verb three rows above
     it and was read exactly once, at mount, so renaming a desk and pressing
     Sync now from inside the very same panel left the line reading "Changed
     since sync" across two successful syncs. It was right again as soon as the
     sheet was reopened, which is the tell for a mount-only read.

     `mdsyncdone` and not `mdsynced`, and that difference is the whole fix: the
     pull-only event never fires for the ordinary case here, which is a sync
     that pushed one document and pulled none.

     Living here rather than in the widget, it also repaints the root's two
     bars — which had the same staleness and no fix, because only one of the two
     surfaces had been given one. Only one is ever mounted, so this is still one
     listener, not two. */
  useEt(() => {
    let alive = true;
    const reread = () => {
      askKindsS().then((shelves) => { if (alive) setKinds(shelves); });
      if (alive && window.mdStorageLocalBytes) setLocalBytes(window.mdStorageLocalBytes());
    };
    window.addEventListener('mdsyncdone', reread);
    return () => { alive = false; window.removeEventListener('mdsyncdone', reread); };
  }, []);
  return { persisted, setPersisted, cloud, localBytes, kinds };
}

/* THE CLOUD LINE, wherever it is drawn. Four answers and not one of them is a
   zero: a bar when the store states an allowance, a plain total when it states
   none, and two different refusals — "we could not ask" and "you are not signed
   in" are different sentences and only one of them is safe to put in front of
   somebody. Absent entirely until the store has answered, because an empty bar
   drawn while we wait is a claim, and the claim is wrong. */
function CloudLineS({ cloud, accent, rule, lead }) {
  const said = { color: inkS(0.45), fontSize: 12.5, lineHeight: 1.5, margin: 0 };
  if (!cloud) return null;
  if (cloud.ok && typeof cloud.allowance === 'number' && cloud.allowance > 0) {
    return <CapacityBarS which="cloud" label="Cloud storage" accent={accent}
      lead={lead} used={cloud.bytes || 0} total={cloud.allowance} />;
  }
  if (cloud.ok && cloud.allowance == null) {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: lead ? 12 : 0,
        padding: '13px 16px', borderBottom: rule }}>
        {lead && <SLead boxH={20}>{lead}</SLead>}
        <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', color: inkS(0.92), fontSize: 15, fontWeight: 550 }}>
          <span className="md-live">{mdBytesSaid(cloud.bytes || 0)}</span> in cloud storage
        </span>
        <p style={{ ...said, marginTop: 3 }}>
          Your store does not say how much room it gives you, so there is no line to draw.
        </p>
        </div>
      </div>);
  }
  if (!cloud.ok) {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: lead ? 12 : 0,
        padding: '13px 16px', borderBottom: rule }}>
        {lead && <SLead boxH={20}>{lead}</SLead>}
        <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', color: inkS(0.92), fontSize: 15, fontWeight: 550 }}>
          {cloud.reason === 'unauthorised'
            ? 'Sign in to see what your account is holding'
            : 'Could not reach your account just now'}
        </span>
        <p style={{ ...said, marginTop: 3 }}>
          Nothing is wrong with what is on this device. This is the number we could not fetch,
          not a number that came back as nothing.
        </p>
        </div>
      </div>);
  }
  return null;
}

/* The glance. Both bars, no verbs, no breakdown — everything you might DO about
   your data is one screen in, and this is only the number worth interrupting a
   scroll for. */
function StorageBarsS({ accent, last }) {
  const light = isLightS();
  const { cloud, localBytes } = useStorageReadingS();
  const rule = `1px solid ${inkS(light ? 0.07 : 0.06)}`;
  return (
    <div data-md-storage-bars="">
      <CloudLineS cloud={cloud} accent={accent} rule={rule} lead={MDIS.cloud(GLYPH(), 17)} />
      {/* This browser. Always drawn — the wall is a constant, so there is
          nothing to wait for and nothing that can fail to answer. */}
      <CapacityBarS which="browser" label="Local browser storage" accent={accent}
        lead={MDIS.laptop(GLYPH(), 17)} used={localBytes || 0}
        total={MD_KEY_WALL_BYTES} last={last} />
    </div>);
}

function StorageWidget({ accent }) {
  const light = isLightS();
  const [asking, setAsking] = useSt(false);
  const [askedNote, setAskedNote] = useSt(null);
  const [transfers, setTransfers] = useSt([]);
  const { persisted, setPersisted, cloud, localBytes, kinds } = useStorageReadingS();

  /* THE SAME QUEUE, THE SAME COMPONENT, A SECOND MOUNT. A pause is durable
     state in md-blobs.ts, so Settings reads what the desk reads and presses the
     very same verbs. Two seconds is the board's cadence too: fast enough for a
     watched chunk bar and idle for the overwhelming ordinary state, which is
     an empty list. */
  const readTransfers = async () => {
    if (typeof window.mdBlobTransfers !== 'function') { setTransfers([]); return; }
    let rows = [];
    try { rows = await window.mdBlobTransfers(); } catch (err) { rows = []; }
    setTransfers(Array.isArray(rows) ? rows : []);
  };
  useEt(() => {
    let alive = true;
    const read = async () => {
      if (!alive) return;
      if (typeof window.mdBlobTransfers !== 'function') { setTransfers([]); return; }
      let rows = [];
      try { rows = await window.mdBlobTransfers(); } catch (err) { rows = []; }
      if (alive) setTransfers(Array.isArray(rows) ? rows : []);
    };
    read();
    const timer = setInterval(read, 2000);
    return () => { alive = false; clearInterval(timer); };
  }, []);

  const onTransferAct = async (what, hash) => {
    const row = transfers.find((one) => one && one.hash === hash);
    const door = { pause: window.mdBlobPause, resume: window.mdBlobResume,
      cancel: window.mdBlobCancel }[what];
    if (typeof door !== 'function') return;
    const ref = row && Array.isArray(row.refs) ? row.refs[0] : undefined;
    const out = what === 'cancel' ? await door(hash, ref) : await door(hash);
    /* Cancel is two acts: the door gives back what reached the store, then the
       owning surface stops promising bytes that will never arrive. Settings is
       the account-wide surface, so it must complete BOTH record and desk refs;
       leaving the first to a sheet that may not be open was the unresolvable
       reference 0.9 exists to prevent, reached from the other direction. */
    const unname = out && out.unname;
    const owner = unname && typeof unname.ref === 'string' ? unname.ref : ref;
    const namedHash = unname && unname.hash ? unname.hash : hash;
    if (what === 'cancel' && out && out.ok && typeof owner === 'string'
      && owner.startsWith('board:') && typeof window.mdBoardForgetFile === 'function') {
      await window.mdBoardForgetFile(owner.slice('board:'.length), namedHash);
    } else if (what === 'cancel' && out && out.ok && typeof owner === 'string'
      && owner.startsWith('record:') && typeof window.mdFileForgetRecord === 'function') {
      window.mdFileForgetRecord(namedHash, owner);
    }
    await readTransfers();
  };

  /* REPORTING AND RETRY, NOT A SECOND REQUEST. md-app.jsx:1128 asks once at
     boot, which is the right moment — before anything has been written. This
     surface says what the answer was, and only a press asks again. */
  const askAgain = async () => {
    setAsking(true);
    setAskedNote(null);
    const granted = window.mdRequestPersistence ? await window.mdRequestPersistence() : null;
    const now = await askPersistedS();
    setPersisted(now);
    setAsking(false);
    if (now !== true) {
      setAskedNote(granted === false
        ? 'The browser said no. It usually says yes once ManDana has been opened on a few different days.'
        : 'This browser will not say. Nothing is lost by asking again later.');
    }
  };

  const health = window.mdStorageHealth ? window.mdStorageHealth() : null;
  const boardHealth = window.mdBoardDbHealth ? window.mdBoardDbHealth() : null;
  const fault = [health && health.lastFault, boardHealth && boardHealth.lastFault]
    .filter(Boolean)
    .sort((a, b) => b.at - a.at)[0] || null;

  const rule = `1px solid ${inkS(light ? 0.07 : 0.06)}`;
  const said = { color: inkS(0.45), fontSize: 12.5, lineHeight: 1.5, margin: 0 };
  /* A kind with nothing in it is left out rather than drawn as a nought. An
     empty cache answers 0, which is a number and not an absence, so the test is
     on the value and not on whether one arrived. */
  const shown = (kinds || []).filter((k) => (k.count || 0) > 0 || (k.bytes || 0) > 0);

  return (
    <div data-md-storage-widget="">
      {/* The same two lines the root draws, said once and drawn twice — this
          screen then carries on into the breakdown the root deliberately
          leaves out. */}
      <CloudLineS cloud={cloud} accent={accent} rule={rule} />
      <CapacityBarS which="browser" label="Local browser storage" accent={accent}
        used={localBytes || 0} total={MD_KEY_WALL_BYTES} />
      <p style={{ ...said, padding: '10px 16px 12px', borderBottom: shown.length ? rule : 'none' }}>
        That line counts what ManDana writes here — your words, your days and your settings.
        Drawings, pictures and files you drop in are kept in a much larger store beside it,
        and are not counted above.
</p>

      {/* What is in it. Hidden entirely rather than drawn as five zeroes: a
          fresh device has nothing to break down, and a column of noughts reads
          as a broken screen rather than as an empty one. */}
      {shown.length > 0 && shown.map((k, i) => (
        <DataLineS key={k.kind} {...k} last={i === shown.length - 1} />
      ))}

      {/* Empty is a sentence, not a card. MdTransfers already owns that choice
          and every status/action word; this mount supplies only account-wide
          rows and the shared queue verbs. */}
      {window.MdTransfers && (
        <div data-md-account-transfers="" style={{ padding: '13px 16px', borderTop: rule }}>
          <span style={{ display: 'block', color: inkS(0.92), fontSize: 15,
            fontWeight: 550, marginBottom: 8 }}>Uploads</span>
          <window.MdTransfers rows={transfers} onAct={onTransferAct} />
        </div>
      )}

      {/* The grant, and the one thing on this widget that is a control. */}
      <div style={{ padding: '13px 16px', borderTop: rule }}>
        <span style={{ display: 'block', color: inkS(0.92), fontSize: 15, fontWeight: 550 }}>
          {persisted === true ? 'Kept by the browser'
            : persisted === false ? 'This browser has not promised to keep your data'
              : 'Whether this browser will keep your data is unknown'}
        </span>
        <p style={{ ...said, marginTop: 3 }}>
          {persisted === true
            ? 'It has promised not to clear ManDana to make room for something else.'
            : persisted === false
              ? 'Without that promise it may clear ManDana to make room — on some browsers after a week or two of not being opened.'
              : 'This browser does not answer the question. Your data is here either way; what is unknown is how long it keeps it.'}
        </p>
        {persisted !== true && (
          <button type="button" data-md-persist-retry="" disabled={asking}
            onClick={asking ? null : askAgain}
            style={{ marginTop: 10, padding: '9px 14px', borderRadius: 11,
              cursor: asking ? 'default' : 'pointer', background: 'transparent',
              border: `1px solid ${inkS(0.14)}`, color: inkS(0.66),
              fontSize: 13.5, fontWeight: 600 }}>
            {asking ? 'Asking…' : 'Ask the browser to keep it'}
          </button>
        )}
        {askedNote && (
          <p role="status" style={{ ...said, marginTop: 9 }}>{askedNote}</p>
        )}
      </div>

      {/* A write that failed. Kept from the sentence this widget replaced,
          because it is the one thing here that is news rather than a reading. */}
      {fault && (
        <div style={{ padding: '13px 16px', borderTop: rule }}>
          <span style={{ display: 'block', color: rgbaS('#FFB3C7', 0.92), fontSize: 14, fontWeight: 600 }}>
            {fault.quota ? 'A save failed: out of space' : `A save failed: ${fault.name}`}
          </span>
          <p style={{ ...said, marginTop: 3 }}>
            It is still on screen and still being retried. Backing up to a file is the way to be
            sure of it.
          </p>
        </div>
      )}
    </div>);
}

/** What this device may say about itself when the server did not answer.

    NOBODY COULD BE ASKED IS NOT A REFUSAL, and md-sync.js:667 returns three
    answers precisely so the difference survives the trip. Its own comment
    names this bug; md-gate.ts:24 exists for the same distinction and honours
    it at :124. This screen read `signedIn` alone, so on 2026-08-31 one
    unanswered /api/auth/me replaced a real account — the device list, the
    username, and the Sync now row — with "Sign in — Already have an account",
    until a page reload. The session had never been lost.

    THE FALLBACK IS THE GATE'S, VERBATIM: what this device durably knows about
    itself. A session mark is the note it wrote when the server last told it
    who it was, which is exactly the fact an unreachable server cannot refresh
    and cannot contradict. A device with no mark keeps the way in on screen —
    stranding somebody behind a blank section is the same mistake pointed the
    other way.

    `reachable` is left as it came, false, so anything that needs to know the
    difference still can. Only `signedIn` is answered from memory. */
function mdAuthHeld(status) {
  if (!status || typeof status !== 'object') return status;
  if (status.reachable !== false) return status;
  const marked = typeof window.mdHasSessionMark === 'function'
    ? !!window.mdHasSessionMark() : !!status.signedIn;
  return { ...status, signedIn: marked };
}

/** The browser's answer, never a guess. `null` is a real third answer: an engine
    with no Storage API says nothing rather than no. */
async function askPersistedS() {
  if (typeof window.mdIsPersisted !== 'function') return null;
  try { return await window.mdIsPersisted(); } catch (err) { return null; }
}

/** What the account is holding, asked of whichever store this device is talking
    to. Every failure comes back as a refusal rather than as a zero. */
async function askCloudUsageS() {
  if (typeof window.mdTransportOpen !== 'function') return { ok: false, reason: 'unreachable' };
  if (typeof window.mdTransportName !== 'function') return { ok: false, reason: 'unreachable' };
  let opened = null;
  try { opened = await window.mdTransportOpen(window.mdTransportName()); }
  catch (err) { return { ok: false, reason: 'unreachable' }; }
  const store = (opened && opened.ok) ? opened.transport : null;
  if (!store || typeof store.blobUsage !== 'function') return { ok: false, reason: 'unreachable' };
  try {
    const usage = await store.blobUsage();
    return (usage && typeof usage === 'object') ? usage : { ok: false, reason: 'unreachable' };
  } catch (err) { return { ok: false, reason: 'unreachable' }; }
}

/* Five kinds, from the catalogue the app already composes.

   A HOME DOCUMENT IS COUNTED ONCE. A day holds every record written on it, so
   summing a home per record would report an account at several times its real
   size — the same mistake usageFromList avoids by keying on the hash
   (md-transport.ts). Distinct home keys, and the byte count is the one
   mdStorageLocalBytes uses: the key plus its value, in UTF-16 code units, which
   is what the wall above is measured in.

   FILES ARE FOUND IN THE WRITING because nothing else knows they are there.
   `mdfile:<id>` is the token a record carries (md-file.ts:51), the bytes live
   in a database of their own with no listing verb, and inventing a size for
   them would be worse than leaving the column blank. */
async function askKindsS() {
  const rows = readCatalogueS();
  const raw = (key) => (typeof window.mdReadRaw === 'function' ? window.mdReadRaw(key) : null);
  const shelf = (kind, label, noun, nouns) =>
    ({ kind, label, noun, nouns, count: 0, bytes: null, homes: new Set(), states: [] });
  const records = shelf('records', 'Records', 'record', 'records');
  const desks = shelf('desks', 'Desks', 'desk', 'desks');
  const crafts = shelf('crafts', 'Crafts', 'Craft', 'Crafts');
  const files = shelf('files', 'Files', 'file', 'files');
  /* NAMED FOR WHAT IT HOLDS, WHICH IS NO LONGER PICTURES. This shelf reports
     the blob store, and the blob store held profile pictures alone until this
     phase moved desk drawings, pasted images, dropped files and Crafts into
     it. The sentence above the list already calls it "a far larger store"
     holding drawings, pictures and dropped files; the row says the same. */
  const pictures = shelf('pictures', 'Drawings, pictures and files', 'file', 'files');
  const named = new Set();

  for (const row of rows) {
    const values = (row && row.values) || {};
    const type = values.rectype;
    const into = type === 'board' ? desks : type === 'craft' ? crafts : records;
    into.count += 1;
    const home = typeof values.storage === 'string' ? values.storage : '';
    if (home && !into.homes.has(home)) {
      into.homes.add(home);
      const text = raw(home);
      if (text != null) into.bytes = (into.bytes || 0) + home.length + text.length;
    }
    if (typeof values.syncstate === 'string' && values.syncstate) into.states.push(values.syncstate);
    const body = (row && typeof row.body === 'string') ? row.body : '';
    for (const hit of body.matchAll(/mdfile:([A-Za-z0-9_-]+)/g)) named.add(hit[1]);
  }

  files.count = named.size;
  /* NO COUNT, DELIBERATELY. blobUsage() counts objects, and an object stopped
     meaning a file the moment 1.13 cut anything over 8 MB into chunks with a
     manifest to name them: one 300 MB PDF is thirty-nine objects. Reporting
     that as "39 files" would be a wrong answer to a question nobody asked,
     printed beside a byte total that is right. The bytes stay; the count goes,
     and the row renders without the clause. */
  pictures.count = null;
  pictures.bytes = await askCacheBytesS();

  const where = (shelved, fallback) => mdShelfSaid(shelved.states, fallback);
  records.where = where(records, 'Nothing written yet');
  desks.where = where(desks, 'Nothing drawn yet');
  crafts.where = where(crafts, 'Nothing here yet');
  files.where = 'In this browser';
  /* Two numbers about two places, said as two places: the count is what the
     account holds, the size on the right is what THIS device is keeping a copy
     of. A line reading "12 pictures · 3 MB" with nothing between them would be
     read as twelve pictures weighing three megabytes, which is not a claim
     anything here can make. */
  pictures.where = pictures.bytes ? 'In your account, kept here for offline' : 'In your account';
  return [records, desks, files, crafts, pictures];
}

function readCatalogueS() {
  if (typeof window.mdLibrarianRows !== 'function') return [];
  try {
    const rows = window.mdLibrarianRows();
    return Array.isArray(rows) ? rows : [];
  } catch (err) { return []; }
}

async function askCacheBytesS() {
  if (typeof window.mdBlobCacheBytes !== 'function') return null;
  try {
    const bytes = await window.mdBlobCacheBytes();
    return typeof bytes === 'number' && Number.isFinite(bytes) ? bytes : null;
  } catch (err) { return null; }
}

/* A picture, or the letters — and there is no third state. Offline, a picture
   set months ago on another device simply cannot be fetched, and initials are
   then the honest answer rather than a broken image or a spinner standing in
   for one. Named for assistive technology because a face is not decoration
   here: it is the only thing in the row identifying whose account this is. */
function SFace({ url, initials, size = 34 }) {
  const box = { width: size, height: size, borderRadius: size / 2, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' };
  if (url) return <img src={url} alt="" aria-label="You" style={{ ...box, objectFit: 'cover' }} />;
  return (
    <span aria-label="You" style={{ ...box, background: inkS(0.10), color: inkS(0.7),
      fontSize: Math.max(9, Math.round(size * 0.38)), fontWeight: 600,
      letterSpacing: '0.04em' }}>{initials}</span>);
}

const SFIELD = (light) => ({ width: '100%', padding: '11px 14px', borderRadius: 12, fontSize: 15,
  background: surfS(0.10), color: inkS(0.92), border: `1px solid ${inkS(light ? 0.10 : 0.08)}`,
  outline: 'none' });

/* Three numbers, in the order they are said aloud — the same grammar the door
   asks with, for the same reason. Every other date in this app is near today,
   where a month grid is the right instrument; a date of birth is thirty or
   fifty years back, where a grid means paging four hundred times.

   Gregorian, and the caption says so: ManDana keeps days in the Hijri calendar
   and this is the one date asked for differently, because a birthday is not
   kept on a lunar clock by the people whose birthday it is. */
const sDobKey = (v) => (v.year && v.month && v.day
  ? `${String(v.year).padStart(4, '0')}-${String(v.month).padStart(2, '0')}-${String(v.day).padStart(2, '0')}`
  : '');

function SDobField({ value, light, accent, onChange, onSave }) {
  const key = sDobKey(value);
  const ok = !!key && (!window.mdPersonDobOk || window.mdPersonDobOk(key));
  const set = (part) => (e) => onChange({ ...value, [part]: e.target.value.replace(/\D/g, '') });
  return (
    <div style={{ padding: '0 16px 14px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.4fr', gap: 8 }}>
        {[['day', 'DAY', 2], ['month', 'MONTH', 2], ['year', 'YEAR', 4]].map(([part, label, len]) => (
          <input key={part} inputMode="numeric" maxLength={len} aria-label={label}
            autoFocus={part === 'day'} value={value[part]} onChange={set(part)}
            onKeyDown={(e) => { if (e.key === 'Enter' && ok) onSave(); }}
            style={{ ...SFIELD(light), textAlign: 'center', letterSpacing: '0.08em' }} />
        ))}
      </div>
      <p style={{ margin: '8px 0 0', color: inkS(0.4), fontSize: 12 }}>
        The Gregorian date.
      </p>
      <button onClick={onSave} disabled={!ok}
        style={{ width: '100%', marginTop: 8, padding: 12, borderRadius: 12, border: 'none',
          cursor: ok ? 'pointer' : 'default',
          background: ok ? rgbaS(accent, 1) : inkS(0.10),
          color: ok ? '#fff' : inkS(0.3), fontSize: 15, fontWeight: 650 }}>
        Save
      </button>
    </div>);
}

const S_MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* A word from a list, shown to a person.

   The document keeps gender as a lower-case token, deliberately: it is a value
   in an allowlist, and a list that has to worry about case is a list with two
   entries per word. But drawing that token is drawing the storage — "male" sat
   under a date of birth that read "17 Apr 1990", one row formatted and the next
   a database dump.

   So the casing is decided where the words are DRAWN, once per surface: here,
   because the sheet is sentence case throughout, and in the mono capitals of the
   landing film for the same list. Nothing written ever passes through this. */
const sWord = (w) => (typeof w === 'string' && w ? w[0].toUpperCase() + w.slice(1) : w);
/** A username split at the seam it wants to wrap on: `['fn.ln.1', '@omana.network']`.
    One part when there is no `@` at all, which is not a username this app mints
    but is what the caller would be handed by a server that changed its mind. */
function sUsernameParts(username) {
  const at = String(username).indexOf('@');
  return at < 0 ? [String(username)] : [String(username).slice(0, at), String(username).slice(at)];
}
/** As a person would say it, not as it is stored. */
function sPersonDob() {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((window.mdPersonGet && window.mdPersonGet('dob')) || '');
  return m ? `${+m[3]} ${S_MON[+m[2] - 1]} ${m[1]}` : '';
}

const CHECK = (c) => MDIS.check(c, 17);

/* ONE INK FOR EVERY MARK (Marwan, 2026-08-31): "Change all chevrons and glyphs
   colors to white matching the text but maybe with 20% less opacity."
   0.92 is the label's ink and 0.72 is a fifth quieter than it, so a mark reads
   as belonging to the row it sits on rather than as a second voice.

   IT IS A FUNCTION AND NOT A CONSTANT, because ink() answers from the theme in
   force at the moment it is asked — hoisted to module scope it would freeze
   whichever theme happened to be on when this file was first evaluated.

   THE ACCENT IS NOT GONE, IT IS SPENT SOMEWHERE ELSE. A word in the right-hand
   column — Dark, En, the method's name — is an ANSWER the person chose, and
   still wears the colour. An arrow saying "there is more behind this row" is
   not an answer, and every row had one. */
const GLYPH = () => inkS(0.72);

/* ── city picker ─────────────────────────────────────────────────────────── */
/* No header of its own: the sheet titles every screen it shows (Polish 5), so
   this and its siblings begin at their content. */
/* THE NAME A PERSON READS, from the two letters the table stores. Intl knows
   every region name the browser ships with, so there is no country table here
   to fall out of date — and no country table to translate either. The code is
   the fallback, because a row saying "XK" is still better than a row saying
   nothing.

   THE ONE NAME WE DO NOT ASK FOR (Marwan, 2026-09-02). Asking the engine buys
   244 names for nothing, and pays for them by handing the naming decision to
   whoever shipped the engine — which today answers PS with "Palestinian
   Territories". This app has already refused that judgement one level down:
   Palestine's forty-four places are hand-compiled here rather than taken from
   a gazetteer (scripts/places-curated.mjs). The country's own name is the same
   decision, so it is written here, in the source, and the lookup never reaches
   the engine for this code.

   It is a name, not a table. One row, so 243 names stay the engine's problem —
   and so this one can only ever change when somebody edits this file. Data
   generated by a build can be lost by a build; a name taken from the platform
   can change underneath us on an update. This can do neither. */
const MD_COUNTRY_OURS = { PS: 'Palestine' };

function mdCountryName(code) {
  if (MD_COUNTRY_OURS[code]) return MD_COUNTRY_OURS[code];
  try { return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code; }
  catch (e) { return code; }
}

/* THE COUNTRIES, NAMED AND ALPHABETICAL. This kept the city table's own order
   while that table held thirty-nine countries, so the list opened on Saudi
   Arabia and Egypt rather than on Afghanistan and Albania — a defensible bias
   in a hand-written list about a pilgrimage.

   At the world's length the argument inverts. Nobody scrolls 244 rows hoping a
   curated top holds their country; they look where the alphabet says the name
   is, and anything else reads as the list being broken. Cities keep the table's
   order — biggest first — because that one is still true. */
function mdCountriesNamed(codes) {
  const seen = new Set();
  const out = [];
  for (const code of codes || []) {
    if (!code || seen.has(code)) continue;
    seen.add(code);
    out.push({ code, name: mdCountryName(code) });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/* ── HOW MANY CITY ROWS ARE WORTH DRAWING ────────────────────────────────────
   The world table answered "only 4 cities??" and asked a new question in the
   same breath. The United States has 3,127 of them and India 3,735, and drawing
   one country whole put 13,633 nodes on the screen and cost 72ms between a
   keystroke and the paint that answered it — measured, on a laptop. A phone is
   several times that, which is a quarter-second lag on every letter and reads
   as a text field that has stopped working.

   Six rows of it, and a scrollbar (Marwan, 2026-08-31, round five). Every
   list is shown WHOLE inside a box of one height — no fifty-row cap, no rows
   held back for a letter, and so no truncation count owed: a scrolling list
   leaves nothing out. The box is what pins the dialog: rows appearing and
   vanishing under a keystroke change what is INSIDE it, never its size, so
   nothing on the screen moves but the rows. The heaviest list a walk can
   reach is England's 686 cities — about 4ms a keystroke by the cap-era
   measurements, a price a settings dialog can pay for standing still.

   A BOX TALLER THAN ITS ROWS IS DEAD AIR: a list that fits inside the one
   height simply stands at its own. */
const MD_LIST_BOX = 264;

/* ── THE TABLE THE PICKER DRAWS FROM ─────────────────────────────────────────
   Two lists wearing one shape (md-cities.js): the fifty-nine cities the app
   boots with, and GeoNames' 31,744 fetched the first time this screen opens.

   THE FETCH IS HUNG ON THE SCREEN, NOT ON THE PICKER. Somebody who opens Your
   location is a press away from opening the steps, and starting a megabyte then
   means the list is already there when they do — while the root, where nobody
   is choosing a city, asks for nothing.

   The seed is memoised rather than rebuilt each render: it carries a per-country
   cache of its own, and a fresh object every keystroke in the search box throws
   that cache away on every letter. */
function useCityTableS(active) {
  const [table, setTable] = useSt(null);
  const seed = useMt(() => (window.mdCityTableFromList
    ? window.mdCityTableFromList(window.MD_CITIES) : null), []);
  useEt(() => {
    if (!active || table || !window.mdWorldCities) return undefined;
    let live = true;
    window.mdWorldCities().then((t) => { if (live && t) setTable(t); });
    return () => { live = false; };
  }, [active, table]);
  return table || seed;
}

/* ── CHOOSING A PLACE, IN THE COUNTRY'S OWN QUESTIONS ────────────────────────
   This was fifty-nine cities in one open list, and Marwan named it on
   2026-08-30: it is the whole screen, it buries everything under it, and
   finding your own city means scrolling past four continents.

   Two rows — Country, then Region/State/City — each opening where it stands
   with a search of its own. Since ROUND FOUR (Marwan, 2026-08-31) they live
   inside the Where-are-you dialog rather than inline on the sheet: unfolded
   there they pushed the calculation method off the screen, and a raised
   phone keyboard covered the very rows being asked. One list is open at a
   time, because two open lists is the wall again in two pieces. The slashed
   label is deliberate: a state, a province, a governorate, an emirate — and,
   where a country files its towns under no division at all, the towns
   themselves — are all the one question after the country, so one field
   carries every country's divisional grammar.

   THE MIDDLE QUESTION IS THE COUNTRY'S OWN DIVISIONS (Marwan, 2026-08-31).
   Egypt asks its 27 governorates, the United States its states — listed whole
   and alphabetical, because ~30 named regions are a list a person reads,
   where 3,127 cities were not. The row only exists where the table knows
   them: a version-1 table, and the boot list, carry no regions and keep the
   two-question walk. A capital GeoNames files under no division at all
   (Nouakchott, Hong Kong) stands in the division list directly, first, so no
   one's capital is hidden behind a question it does not answer.

   AND EVERY PICK IS AN ANSWER (Marwan, 2026-08-31). A country applies its
   capital, a division its seat, a loose city itself — each the moment it is
   chosen, so the row above is never reading one country while the walk
   stands in another. THE WALK ENDS AT THE DIVISION (Marwan, 2026-08-31,
   round six): the town-by-town third question died — across the shipped
   table, nine in ten divisions put their farthest town within ten minutes'
   solar time of their seat, and the crosshair is the exact answer for
   whoever wants one. Re-picking the answer already in force moves nothing —
   somebody in Alexandria opening Egypt to browse is not asking to be sent to
   Cairo.

   IT STARTS FROM WHERE YOU ALREADY ARE. The country is seeded from the place
   in force, and the division in force is read off it, so the checks are
   already on your own answers. A precise fix has no country attached — there
   is no reverse geocoder here — so that is the one case where the first
   question really is the first question.

   THE LISTS STAND WHOLE AND SCROLL (Marwan, 2026-08-31, round five). Rows
   used to be held back until the first letter — and since every letter then
   changed the dialog's height, the centred card jumped with every keystroke.
   Each open list now stands complete inside a box of one height
   (MD_LIST_BOX) that scrolls, the search above it narrowing what the box
   holds: the picker everybody already knows how to use. The search still
   only appears once a list outgrows a glance.

   `startOpen` opens the country question the moment the steps appear — the
   by-hand gesture, where the person has already said they want to choose.
   The dialog a deep link opens keeps every list at rest instead: the link
   promised the place, not a keyboard grab.

   THERE IS NO DETECT ROW IN HERE. "Use this device's location" stood at the
   foot of these steps until 2026-08-31, when the crosshair on the row above
   became permanent and the row itself became the walkthrough's own door — a
   row in here would be the same door drawn a third time, buried under the
   question it answers. */
function PlacePicker({ accent, loc, table, onPick, startOpen }) {
  const light = isLightS();
  const [country, setCountry] = useSt(loc && loc.country ? loc.country : null);
  /* The division picked HERE, this visit; the one in force is read off `loc`
     below, so a fresh mount already wears the person's own answer. */
  const [division, setDivision] = useSt(null);
  const [open, setOpen] = useSt(startOpen ? 'country' : null); // 'country'|'region'|null
  const [q, setQ] = useSt('');
  /* A mount value is not enough if the steps already stand — a deep-linked
     dialog, held open, when a later gesture asks for the country question.
     The prop TURNING true is the ask. */
  useEt(() => { if (startOpen) { setQ(''); setOpen('country'); } }, [startOpen]);

  const codes = table ? table.countries : [];
  const countries = useMt(() => mdCountriesNamed(codes), [codes]);
  const shownCountries = useMt(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return countries;
    return countries.filter((c) => c.name.toLowerCase().includes(needle)
      || c.code.toLowerCase() === needle);
  }, [countries, q]);

  const cities = useMt(() => (table && country ? table.cities(country) : []), [table, country]);
  const parts = useMt(() => (window.mdCityDivisions
    ? window.mdCityDivisions(cities) : { loose: cities, divisions: [] }), [cities]);
  /* THE MIDDLE QUESTION EXISTS WHEN IT WOULD CHOOSE ANYTHING: more than one
     row, counting divisions and loose cities together. One division holding
     every town is no question — its seat is the country's own answer — and
     so is a single loose capital. */
  const hasMiddle = parts.divisions.length + parts.loose.length > 1;
  const chosenDivision = useMt(() => {
    const said = division || (loc && loc.country === country ? loc.region || null : null);
    return said && parts.divisions.some((d) => d.region === said) ? said : null;
  }, [division, loc, country, parts]);

  /* Folded, ranked, and aliased: GeoNames calls them Makkah and Madinah, and
     somebody typing English types Mecca. See md-cities.js. */
  const matchedDivisions = useMt(() => (window.mdDivisionSearch
    ? window.mdDivisionSearch(parts.divisions, q) : parts.divisions), [parts, q]);
  const matchedLoose = useMt(() => (window.mdCitySearch
    ? window.mdCitySearch(parts.loose, q) : parts.loose), [parts, q]);

  const show = (which) => { setQ(''); setOpen(open === which ? null : which); };
  /* A PLACE, NOT A TABLE ROW. The v2 table marks capitals and seats with a
     `flag` for its own purposes; a location object is a persisted document,
     and an undocumented field would sit in it for the life of the device. */
  const asPlace = ({ flag, ...c }) => ({ ...c, source: 'manual' });

  /* EVERY PICK IS AN ANSWER — the handlers, not just the words. `settled`
     tells the sheet whether the walk is over: a division always ends it now,
     a loose city always did, and a country with no middle question to ask
     ends it by itself. Re-picking what is already in force applies nothing. */
  const pickCountry = (code) => {
    setQ('');
    const cs = table ? table.cities(code) : [];
    const dv = window.mdCityDivisions ? window.mdCityDivisions(cs)
      : { loose: cs, divisions: [] };
    const middle = dv.divisions.length + dv.loose.length > 1;
    setCountry(code); setDivision(null);
    if (!loc || loc.country !== code) {
      const seat = window.mdCountrySeat ? window.mdCountrySeat(cs) : cs[0];
      if (seat) onPick(asPlace(seat), !middle);
      if (!middle) return;
    }
    setOpen(middle ? 'region' : null);
  };
  const pickDivision = (d) => {
    setQ('');
    setDivision(d.region);
    const changes = !(loc && loc.country === country && (loc.region || '') === d.region);
    if (changes && d.seat) onPick(asPlace(d.seat), true);
    else setOpen(null);
  };

  /* THE SEARCH IS THE DOOR: focused the moment its step opens, because the
     rows are waiting on it. A SEARCH OVER FOUR ROWS IS STILL NOISE — every
     step earns one past eight entries, and the country step always carries
     one: every real table, boot or world, holds dozens. The threshold is on
     the list's full length, not the filtered one, so the box does not vanish
     underneath somebody who is typing into it. */
  const search = (nouns, wanted) => (!wanted ? null : (
    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${nouns}`}
      aria-label={`Search ${nouns}`} autoFocus
      style={{ width: '100%', padding: '11px 14px', borderRadius: 12, fontSize: 15,
        background: surfS(0.10), color: inkS(0.92), border: `1px solid ${inkS(light ? 0.10 : 0.08)}`,
        outline: 'none', margin: '10px 0 4px' }} />
  ));
  const noMatch = (shown) => (!!q.trim() && shown === 0 && (
    <p style={{ color: inkS(0.4), fontSize: 12.5, padding: '10px 0' }}>No match</p>
  ));

  /* NO FOOTNOTE. Two true things used to stand under this list — which table
     it is showing, and GeoNames' CC BY 4.0 credit — and both were faded
     paragraphs under a question somebody was in the middle of answering. The
     attribution still ships beside the data it licenses
     (public/data/CITIES-LICENSE.txt); the short-list warning is on the Needed
     Explainers list, to come back as a control rather than as more text. */

  /* The one height that pins the dialog — see MD_LIST_BOX above. Keyed to the
     FULL list, never the filtered one, so typing cannot resize it. It answers
     the ROOM, though: the ask's room is a size container, and cqh is its
     height, so a room too short for six rows — a tablet-portrait keyboard
     leaves ~680px — shrinks the box before the dialog has to scroll. The
     358px is everything around the box (title, the two rows, search, Done,
     margins), and the 140px floor is where shrinking stops helping and the
     room's own scroll takes over. Only a keyboard moves cqh; a keystroke
     never does. */
  const listBox = (fullLen, rows) => (
    <div data-md-list-box="" style={fullLen > 6
      ? { height: MD_LIST_BOX, minHeight: 140, maxHeight: 'calc(100cqh - 358px)',
        overflowY: 'auto' }
      : { height: 'auto', overflowY: 'auto' }}>
      {rows}
    </div>
  );

  return (
    <div data-md-place-picker>
      <SRow label="Country" sub={country ? mdCountryName(country) : 'Not chosen yet'}
        accent={accent} last={!hasMiddle && open !== 'country'}
        onClick={() => show('country')} />
      {open === 'country' && (
        <div data-md-country-list style={{ padding: '0 16px 6px' }}>
          {search('countries', true)}
          {listBox(countries.length, <>
            {noMatch(shownCountries.length)}
            {shownCountries.map((c) => (
            <SRow key={c.code} label={c.name} accent={accent}
              right={country === c.code ? CHECK(GLYPH()) : null}
              last={c === shownCountries[shownCountries.length - 1]}
              onClick={() => pickCountry(c.code)} />
            ))}
          </>)}
        </div>
      )}
      {hasMiddle && (
        <SRow label="Region/State/City"
          sub={chosenDivision
            || (loc && loc.country === country && !loc.region
                && window.mdPlaceName(loc)) || (
              parts.divisions.length
                ? `${parts.divisions.length.toLocaleString('en-US')} ${parts.divisions.length === 1 ? 'region' : 'regions'}`
                : `${parts.loose.length.toLocaleString('en-US')} ${parts.loose.length === 1 ? 'city' : 'cities'}`)}
          accent={accent} last={open !== 'region'}
          onClick={() => show('region')} />
      )}
      {open === 'region' && hasMiddle && (
        <div data-md-region-list style={{ padding: '0 16px 6px' }}>
          {search('regions', parts.divisions.length + parts.loose.length > 8)}
          {listBox(parts.divisions.length + parts.loose.length, <>
            {noMatch(matchedLoose.length + matchedDivisions.length)}
            {matchedLoose.map((c, i) => (
              /* A division-less capital is a CITY row standing among the
                 divisions: picking it is a full answer, so it closes the walk. */
              <SRow key={`loose-${c.city}-${i}`} label={window.mdPlaceName(c)}
                accent={accent}
                right={loc && loc.city === c.city && !loc.region ? CHECK(GLYPH()) : null}
                last={matchedDivisions.length === 0 && i === matchedLoose.length - 1}
                onClick={() => onPick(asPlace(c), true)} />
            ))}
            {matchedDivisions.map((d, i) => (
              <SRow key={d.region} label={d.region} accent={accent}
                right={chosenDivision === d.region ? CHECK(GLYPH()) : null}
                last={i === matchedDivisions.length - 1}
                onClick={() => pickDivision(d)} />
            ))}
          </>)}
        </div>
      )}
    </div>);
}

/* ── the sheet ───────────────────────────────────────────────────────────── */
/* ── Hijri ±day stepper ──────────────────────────────────────────────────── */
function HijriAdjuster({ accent, value, onChange }) {
  const light = isLightS();
  const btn = (label, glyph, delta, disabled) => (
    <button onClick={() => onChange(value + delta)} disabled={disabled} aria-label={label}
      style={{ width: 34, height: 34, borderRadius: 'var(--md-radius-control)', cursor: disabled ? 'default' : 'pointer',
        border: `1px solid ${inkS(light ? 0.12 : 0.10)}`, background: surfS(0.10),
        color: inkS(disabled ? 0.22 : 0.8), fontSize: 18, fontWeight: 600, lineHeight: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{glyph}</button>
  );
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {btn('Earlier', MDIS.minus(inkS(value <= -2 ? 0.22 : 0.8), 18), -1, value <= -2)}
      <span style={{ minWidth: 34, textAlign: 'center', fontVariantNumeric: 'tabular-nums',
        color: value === 0 ? inkS(0.5) : rgbaS(accent, 1), fontSize: 15, fontWeight: 650 }}>
        {value > 0 ? `+${value}` : value}
      </span>
      {btn('Later', MDIS.plus(inkS(value >= 2 ? 0.22 : 0.8), 18), 1, value >= 2)}
    </span>);
}


/* ── WHAT THE WALKTHROUGH SAYS, stage by stage ───────────────────────────────
   A PURE function over plain facts, in the house pattern (mdSyncSaid,
   mdTrashRowSay): every sentence the allow-location walkthrough can show is
   provable without a browser, a dialog, or a device.

   THE WALKTHROUGH ASKS THE PERSON BEFORE IT ASKS THE BROWSER (Marwan,
   2026-08-31). A permission prompt that arrives unannounced reads as the app
   doing something to you; a sentence first — what will be asked, what it is
   for, what never leaves the device — makes the browser's dialog the second
   half of a conversation the person already agreed to.

     ask      the sentence, then Allow / Choose a city
     busy     both buttons held, the primary saying which wait this is: the
              person's own answer in the browser, or a radio still looking
     found    what was found and what it changed — Done, or Not my city
     failed   why not, in its own words — a block carries the way to turn it
              back on IN THIS BROWSER (mdGeoSettingsPath, because a web page
              cannot open an OS settings pane), and every failure's primary is
              the manual steps, which are the way that works right now

   `pick` routes the PRIMARY of a failure into the country steps;
   `cancelPick` routes the QUIET button there — every stage carries one manual
   door, because the row that used to open the steps starts this walkthrough
   now (Marwan, 2026-08-31) and a walkthrough with no way out into choosing
   would hold the person's own answer hostage. Decided here so the dialog
   component stays words-in, buttons-out. */
function mdLocateSaid(ask, ua) {
  const a = ask || {};
  if (a.stage === 'found') {
    return {
      /* The quiet button is the one manual door a granted permission ever
         sees — that device never meets the ask stage, and the row that used
         to open the steps starts this walkthrough instead. */
      title: 'Found you', confirm: 'Done', cancel: 'Not my city', cancelPick: true,
      body: `Prayer times now follow ${a.city || 'your location'}.`
        + (a.moved ? ` The calculation method moved to ${a.moved}.` : ''),
    };
  }
  if (a.stage === 'failed') {
    if (a.why === 'blocked') {
      return {
        title: 'Location is off for ManDana',
        body: 'Your browser is not allowing it. To turn it back on: '
          + `${window.mdGeoSettingsPath(ua)}. Or choose your city yourself.`,
        confirm: 'Choose a city', cancel: 'Close', pick: true,
      };
    }
    if (a.why === 'unsupported') {
      return {
        title: 'This browser cannot share a location',
        body: 'You can choose your country and city yourself instead.',
        confirm: 'Choose a city', cancel: 'Close', pick: true,
      };
    }
    return {
      title: 'Could not find you',
      body: 'Your device could not find you just now. Try again in a minute, '
        + 'or choose your city yourself.',
      confirm: 'Choose a city', cancel: 'Close', pick: true,
    };
  }
  return {
    title: 'Allow location?',
    body: 'Your browser will ask once. Prayer times then follow where you are, '
      + 'automatically — and your location never leaves this device.',
    /* Somebody who reached this sentence wanting to pick a city by hand is
       not refusing anything — the quiet button is that door. Declining is
       still one gesture: Escape, or a press outside. */
    confirm: 'Allow', cancel: 'Choose a city', cancelPick: true,
    working: a.wait === false ? 'Finding you…' : 'Waiting for your answer in the browser…',
  };
}

/* The walkthrough, worn as the app's one ask shape. Stages are plain data on
   `ask`; the words come from mdLocateSaid; the only wiring here is which
   button means what. */
function LocateAsk({ ask, accent, onAllow, onPick, onClose }) {
  const said = mdLocateSaid(ask, navigator.userAgent);
  const terminal = !!ask && (ask.stage === 'found' || ask.stage === 'failed');
  return (
    <AskS open={!!ask} accent={accent} busy={ask && ask.stage === 'busy'}
      attr={{ 'data-md-locate-ask': ask ? ask.stage : '' }}
      label={said.title} title={said.title}
      confirm={said.confirm} working={said.working} cancel={said.cancel}
      onConfirm={terminal ? (said.pick ? onPick : onClose) : onAllow}
      onCancel={said.cancelPick ? onPick : onClose} onDismiss={onClose}>
      <p style={askSaid()}>{said.body}</p>
    </AskS>
  );
}

/* ── WHERE ARE YOU, AS A DIALOG ──────────────────────────────────────────────
   ROUND FOUR (Marwan, 2026-08-31): the three questions left the sheet and
   took the app's one ask shape — the plane, the dismiss layer, the glass, the
   viewport-pinned room — so the sheet stays a sheet and the keyboard covers
   nothing.

   COUNTRY AND REGION ARE REQUIRED; THE CITY IS OPTIONAL (Marwan, 2026-08-31).
   Every pick applies itself the moment it lands, so Done is a door and not a
   verb: it holds while a required question is open, closes the dialog once
   both are answered, and applies nothing of its own. It holds — it does not
   trap: Escape and a press outside decline exactly as they always did,
   because a person backing out of a question they cannot yet answer must be
   allowed out.

   `mdPlaceAnswered` is the requirement over plain data: a country, and —
   where the table gives the country a middle question — a division the table
   recognises, or a loose city (Nouakchott, or any town of a country that
   files its towns under no divisions) standing for itself. A region-less
   city that BELONGS to a division (an old fix naming Kiffa and no more)
   still owes the division; a country with no middle question to ask cannot
   be owed one. */
function mdPlaceAnswered(loc, table) {
  if (!loc || !loc.country) return false;
  const cities = table ? table.cities(loc.country) : [];
  const parts = window.mdCityDivisions ? window.mdCityDivisions(cities)
    : { loose: cities, divisions: [] };
  if (parts.divisions.length + parts.loose.length <= 1) return true;
  if (loc.region) return parts.divisions.some((d) => d.region === loc.region);
  return !!loc.city && parts.loose.some((c) => c.city === loc.city);
}

function PlaceAsk({ open, accent, loc, table, startOpen, onPick, onClose }) {
  return (
    <AskS open={open} accent={accent} attr={{ 'data-md-place-ask': '' }}
      label="Where are you?" title="Where are you?" pinned
      confirm="Done" held={!mdPlaceAnswered(loc, table)} cancel={null}
      onConfirm={onClose} onCancel={onClose} onDismiss={onClose}>
      {/* The same inner glass a section wears on the sheet, so the rows read
          as the grouped card they have always been. */}
      <GlassS radius={18} alpha={0.08} style={{ overflow: 'hidden' }}>
        <PlacePicker accent={accent} loc={loc} table={table}
          startOpen={startOpen} onPick={onPick} />
      </GlassS>
    </AskS>
  );
}

/* ── WHAT SYNC NOW SAYS ──────────────────────────────────────────
   A PURE function over plain facts, in the house pattern (mdTrashRowSay,
   mdWhereSay): every state this row can be in is provable without a network, a
   server, or a second device.

   THE LABEL IS THE ANSWER, NOT THE VERB (Marwan, 2026-08-30). "Sync now" over
   a device with nothing to send is a control offering work that does not
   exist, and pressing it teaches nothing — the round trip returns and the row
   says exactly what it said before. So the row reads its own state, and only
   wears the verb when there is something for the verb to do:

     settled   `Synced`     nothing is waiting; a reading, and not pressable
     waiting   `Sync now`   this device holds changes the server has not seen
     working   `Syncing…`   the press landed and is still running
     refused   `Sync now`   it did not work, said in words, and pressable again

   Which is the grammar the desk's ⋯ menu already uses for the same verb
   (md-board.jsx, paintSyncItem): same states, same disabled rest, and the same
   turning glyph while it is in the air.

   `can` — is there anything a press would do — is decided here rather than at
   the row, so the question has one answer and not two.

   `pending` is a count of documents this device holds that the server has not
   acknowledged — mdSyncStates(), read at the surface. A NEGATIVE or nonsense
   count is treated as none rather than drawn: a broken number should make this
   row quiet, not alarming. And it OUTRANKS the last sync's report, because a
   change made while the sync ran leaves the device behind again, and `Synced`
   is the one word this row must never say untruthfully. */
function mdSyncSaid(state) {
  const s = state || {};
  const whole = (n) => (typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0);
  if (s.busy) {
    return { label: 'Syncing…', sub: 'Sending and fetching changes', tone: 'working', can: false };
  }
  if (s.error) return { label: 'Sync now', sub: s.error, tone: 'refused', can: true };
  /* A DOCUMENT THE SERVER DECLINED IS A REFUSAL, and it outranks the count for
     the same reason the transport error above it does: a number under a press
     that did not fully work reads as though the press worked.

     This is main's fix (2026-09-01) rebuilt in this row's grammar. `mdSync` has
     named every refused document since Phase B; the old row printed only the
     two counts, so a document the account refused every single time read as a
     quiet success. In THIS grammar it lands one step worse — a refused document
     is still unsent, so `pending` never falls, and the row would say "3 changes
     are waiting to go out" for ever, which is a promise that pressing sends
     them. Refusal is the one state left that still carries a caption after
     round eight, so this is exactly where the sentence belongs.

     THE COUNT, NOT THE KEYS. A key is `events:2026-08-31`, this app's private
     spelling of a day; putting it in front of somebody answers a question they
     did not ask in a language they do not read. The number is what separates a
     success from a stall, and what makes pressing twice mean something. */
  const declined = Array.isArray(s.told && s.told.refused) ? s.told.refused.length : 0;
  if (declined > 0) {
    return { label: 'Sync now', tone: 'refused', can: true,
      sub: `${declined} refused — ${declined === 1 ? 'it is' : 'they are'} still on this device` };
  }
  const pending = whole(s.pending);
  if (pending > 0) {
    return { label: 'Sync now', tone: 'waiting', can: true,
      sub: `${pending} ${pending === 1 ? 'change is' : 'changes are'} waiting to go out` };
  }
  if (s.told) {
    /* A sync that moved nothing still happened — somebody pressed a button and
       is owed a sentence, and "0 in, 0 out" is arithmetic rather than one. */
    const inn = whole(s.told.pulled);
    const out = whole(s.told.pushed);
    return { label: 'Synced', tone: 'settled', can: false,
      sub: inn + out === 0 ? 'Just now — nothing needed sending'
        : `Just now — ${inn} in, ${out} out` };
  }
  return { label: 'Synced', sub: 'Everything is up to date', tone: 'settled', can: false };
}

/* ── THE THREE ANSWERS TO "which theme" ──────────────────────────────────────
   Light and Dark are choices. System is a standing instruction, and it is the
   one most people want without knowing it exists: a phone that goes dark at
   sunset should take the app with it.

   The order is Light, Dark, System deliberately — the two concrete answers
   first, then the one that defers to something else. A list that led with
   System would ask a person to understand delegation before it let them pick a
   colour. */
const S_THEMES = [
  ['light', 'Light', 'Pale background, dark text'],
  ['dark', 'Dark', 'Deep background, light text'],
  ['system', 'System', 'Follows your device, morning to evening'],
];
const S_THEME_WORD = Object.fromEntries(S_THEMES.map(([id, word]) => [id, word]));

/* THE TWO ASR OPINIONS, in the order the shadow grows. Both are offered, both
   are named, and neither is described as better or as the default — the words
   say what is measured, and the times in the Today list underneath say what
   that comes to today. */
const ASR = [
  ['shafi', "Shafi'i", 'Asr begins when a shadow is the same length as the object'],
  ['hanafi', 'Hanafi', 'Asr begins when a shadow is twice the object’s length'],
];

/* Every screen this sheet can draw, and the name and glyph it wears at the top.
   ONE map: the header reads it for the title, the way-back reads it for the
   name of the screen underneath, and SETTINGS_VIEWS is its own key list — so a
   screen cannot be added in one place and forgotten in another, which is the
   mistake this table exists to make impossible. */
const S_SCREEN = {
  root: ['Settings', 'gear'],
  method: ['Calculation method', 'sun'],
  workflows: ['Workflows', 'workflow'],
  trash: ['Trash', 'trash'],
  data: ['Your data', 'database'],
  profile: ['Your profile', 'person'],
  devices: ['Passkeys & devices', 'key'],
};
const SETTINGS_VIEWS = new Set(Object.keys(S_SCREEN));
/* `place` was a screen of its own for a day and `city` for a year before it,
   and both are in links people already hold — a shared link is a promise, and
   removing a screen does not release us from it. Both fall through
   cleanSettingsView to 'root', and the sheet reads the ORIGINAL initialView to
   open the two steps there, so the link still lands on the thing it named. */
const S_VIEW_ALIAS = { city: 'place' };
const cleanSettingsView = (value) => {
  const named = S_VIEW_ALIAS[value] || value;
  return SETTINGS_VIEWS.has(named) ? named : 'root';
};

function SettingsSheet({ accent, onClose, onLocation, onCalc, onHijriAdjust, onAuthChange, onSignedOut,
  theme, onTheme, initialView = 'root', onView, phoneDocument = false, deskId = null }) {
  const light = isLightS();
  /* Sub-views are frames of the shared walk (useMdFrames, N-6) — the same
     one-per-Escape mechanics the two workspace windows use, with the root as
     the ground frame. A deep link's initialView reseeds the walk; an ordinary
     re-render never does. */
  const frames = window.useMdFrames({ id: cleanSettingsView(initialView) }, [initialView],
    { ride: 'settings-frame', ground: 1 });
  const view = frames.frame ? frames.frame.id : 'root';
  const goView = (next) => {
    const clean = cleanSettingsView(next);
    if (clean === view) return;
    /* Root is the ground frame — the surface itself. Leaving it PUSHES, so
       the sub-view rides the journal and one Back steps back out (N-14);
       returning pops the walk; sub-view to sub-view replaces, staying one
       Back deep. The old shape replaceTop'd everything, which is why Back
       used to close the whole sheet from the city list. */
    if (clean === 'root') frames.pop();
    else if (view === 'root') frames.push({ id: clean });
    else frames.replaceTop({ id: clean });
    if (onView) onView(clean);
  };
  /* A screen opened FROM a screen, one frame deeper — Trash from Manage your
     data, the way a workflow already opens from the Workflows list. goView
     replaces at this depth, which is right for sibling screens reached from the
     root and wrong for a screen that is INSIDE another one: replacing would put
     Back on the root and quietly lose the screen you came from. */
  const pushView = (next) => {
    const clean = cleanSettingsView(next);
    if (clean === view || clean === 'root') return;
    frames.push({ id: clean });
    if (onView) onView(clean);
  };
  /* A workflow is a screen of its own, one frame DEEPER than the list
     (Polish 5): its way out and Escape step back to Workflows, never over it. It is
     not a deep-linkable view — recents recorded 'workflows' when the list
     opened — so onView is never told about it and SETTINGS_VIEWS never
     admits it. */
  const openFlow = (id) => frames.push({ id: 'workflow', flowId: id });
  const stepBack = () => frames.pop();

  const trashCount = typeof window.mdTrashList === 'function'
    ? window.mdTrashList().length : 0;

  /* ── Your data ────────────────────────────────────────────────────────────
     The reading itself lives in <StorageWidget> above, which holds its own
     state because every one of its answers arrives from somewhere different
     and at a different moment. What is left here is the file input the two
     backup rows share. */
  const fileRef = useRf(null);
  const [dataNote, setDataNote] = useSt(null);

  /* Escape steps back out of a sub-view before it closes the sheet — losing the
     whole surface because you wanted to leave the city list would be rude. The
     ladder is ONE layer on the dismiss arbiter: the arbiter decides WHO is on
     top, this surface decides WHAT a dismissal means. A field being edited
     (the city search, a code input) blurs on the first press — the arbiter's
     two-step rule — and the ladder steps on the second. One frame per press:
     from a workflow's own screen the first Escape lands on the Workflows
     list, not over it (Polish 5). */
  window.useMdDismissLayer(() => {
    if (view !== 'root') stepBack(); else onClose && onClose();
  });
  const [busy, setBusy] = useSt(null);            // 'precise' | 'detect' | auth verbs
  const [bumped, bump] = useSt(0);
  /* A NOTE BELONGS TO THE SCREEN THAT RAISED IT. One `note` for the whole
     sheet read correctly while the sheet was one long page. Now that it is
     eight screens it was a lie: a location this device refused, asked for on
     Your location, followed the walk home and sat under a Location row on the
     root that nobody had pressed.

     Kept WITH the frame it was raised on and read back only there — rather
     than cleared by an effect on the way out, which lands a commit late and
     shows the stale sentence on the new screen for a frame first. */
  const [noted, setNoted] = useSt(null);          // { view, text } | null
  const note = noted && noted.view === view ? noted.text : null;
  const setNote = (text) => setNoted(text == null ? null : { view, text });

  // Account. `auth` is null until the first status check answers, so the section
  // shows nothing rather than flashing "signed out" at someone who is signed in.
  const [auth, setAuth] = useSt(null);
  const [syncNote, setSyncNote] = useSt(null);
  /* A refusal is not a note. They were one string, so "Could not sync just now"
     and "Synced — 3 in, 0 out" were the same field and the row could not tell
     them apart — which is exactly the distinction mdSyncSaid exists to draw. */
  const [syncError, setSyncError] = useSt(null);
  const [linkCode, setLinkCode] = useSt(null);
  const [linkNote, setLinkNote] = useSt(null);
  const [pairOpen, setPairOpen] = useSt(false);
  const [pairInput, setPairInput] = useSt('');
  const [pairNote, setPairNote] = useSt(null);
  const [linking, setLinking] = useSt(false);
  const [linkInput, setLinkInput] = useSt('');
  const [passkeyNote, setPasskeyNote] = useSt(null);
  const [confirmDelete, setConfirmDelete] = useSt(false);

  /* Linked devices. Two facts, one list: the roster says which devices are on
     this account and what they are called, and presence says which of them is
     awake. `peers` follows the room rather than the sheet — a device arriving
     while Settings is open must show up without it being reopened. */
  const [renaming, setRenaming] = useSt(null);    // a device id, or null
  const [renameInput, setRenameInput] = useSt('');
  /* Which profile field has its field open, or null. One at a time, and the
     row is its own toggle — the shape a device rename already uses here. */
  const [editing, setEditing] = useSt(null);      // 'name' | 'dob' | 'gender' | null
  const [draftName, setDraftName] = useSt('');
  const [draftDob, setDraftDob] = useSt({ day: '', month: '', year: '' });
  const [face, setFace] = useSt(null);            // an object URL, or null
  const readPresence = () => {
    const structured = window.mdLivePresence ? window.mdLivePresence() : [];
    const byId = new Map((Array.isArray(structured) ? structured : []).map((peer) => [peer.deviceId, peer]));
    /* Compatibility with a transport/test/older shell that still exposes only
       the id list. Structured presence wins when both know the same device. */
    const legacy = window.mdLivePeers ? window.mdLivePeers() : [];
    for (const deviceId of Array.isArray(legacy) ? legacy : []) {
      if (!byId.has(deviceId)) byId.set(deviceId, { deviceId, activeDesk: null, lastSeen: Date.now() });
    }
    return [...byId.values()];
  };
  const [presence, setPresence] = useSt(readPresence);
  useEt(() => {
    const onPeers = () => setPresence(readPresence());
    window.addEventListener('mdlivepeers', onPeers);
    return () => window.removeEventListener('mdlivepeers', onPeers);
  }, []);

  /* THIS DEVICE FIRST, then the rest by name. This device is never in the peer
     table — nothing lists itself — so its own row is settled here rather than
     read from the room, and a list where the machine you are holding is the one
     unmarked row would read as broken. */
  const devices = useMt(() => {
    /* mdDevicesLinked, NOT mdDevicesRead. A removed device stays in the document
       as a tombstone so the removal can travel to the other devices
       (app/md-devices.ts); drawing the document would take a row off the screen
       that pressed × and leave it on every other one. */
    const held = window.mdDevicesLinked ? window.mdDevicesLinked() : {};
    const mine = window.mdDeviceId ? window.mdDeviceId() : '';
    const liveById = new Map(presence.map((peer) => [peer.deviceId, peer]));
    return Object.entries(held)
      .map(([id, row]) => {
        const peer = liveById.get(id);
        return { id, name: row.name, self: id === mine, live: id === mine || !!peer,
          activeDesk: peer ? peer.activeDesk : null };
      })
      .sort((a, b) => (a.self !== b.self ? (a.self ? -1 : 1)
        : a.name.localeCompare(b.name) || a.id.localeCompare(b.id)));
  }, [presence, renaming, note, syncNote, bumped]);

  const doRename = () => {
    const id = renaming;
    if (!id || !window.mdDeviceRename) return;
    window.mdDeviceRename(id, renameInput);
    setRenaming(null);
    setRenameInput('');
  };

  /* Which device the × was pressed on — `{id, name}`, or null. The NAME is held
     alongside the id and not looked up when the dialog draws, because the row it
     came from leaves the list the moment the removal lands, and a confirmation
     that loses the name of the thing it is confirming mid-press would be worse
     than one that never had it. */
  const [removing, setRemoving] = useSt(null);
  const [removeNote, setRemoveNote] = useSt(null);

  const doRemove = async () => {
    if (!removing || busy === 'devremove') return;
    setRemoveNote(null);
    setBusy('devremove');
    const res = window.mdDeviceRevoke ? await window.mdDeviceRevoke(removing.id) : null;
    setBusy(null);
    /* THE SURFACE BELIEVES THE ANSWER, NOT THE PRESS. mdDeviceRevoke asks the
       server before it touches the document, so a refusal here means the device
       is still signed in — and the list is the only place anybody would look. */
    if (!res || !res.ok) { setRemoveNote((res && res.error) || 'failed'); return; }
    setRemoving(null);
    /* The rename field may be standing open on the row that just left. */
    if (renaming === removing.id) { setRenaming(null); setRenameInput(''); }
    bump((n) => n + 1);
  };

  /* Signing out everything else. Two pieces of state and not one: `othersAsk` is
     the dialog standing open, `othersNote` is what the row says afterwards —
     they are separate because the answer outlives the dialog. A refusal keeps
     the dialog (there is something to try again), a success closes it and leaves
     the count on the row, where the person will look next. */
  const [othersAsk, setOthersAsk] = useSt(false);
  const [othersNote, setOthersNote] = useSt(null);

  const doSignOutOthers = async () => {
    if (busy === 'others') return;
    setOthersNote(null);
    setBusy('others');
    const res = window.mdSignOutOthers ? await window.mdSignOutOthers() : null;
    setBusy(null);
    /* A FAILURE MUST NEVER READ AS SUCCESS. Believing the other screens are out
       when they are not is the one wrong answer this row can give, so the note
       is written from what the server said and the dialog stays open. */
    if (!res || !res.ok) {
      setOthersNote(MD_SIGN_OUT_OTHERS_NOTE[(res && res.error) || 'failed']
        || MD_SIGN_OUT_OTHERS_NOTE.failed);
      return;
    }
    /* AND THE AWAKE ONES ARE TOLD, in this order and not the other. A socket is
       authenticated once, at the upgrade, so a device whose session just went
       keeps relaying documents through the room until its next sync gets a 401.
       The frame closes that in the same second — the same reason the Delete
       button publishes `evict`.

       AFTER the server agreed, never before: telling devices they are out while
       the call is still in flight would sign them out off the back of something
       that might yet fail. And a room that cannot be reached is not a failure —
       the database is the fact, this is the courtesy. */
    if (typeof window.mdLivePublish === 'function') {
      try { await window.mdLivePublish({ t: 'signout' }); } catch (e) { /* next sync tells them */ }
    }

    setOthersAsk(false);
    const n = Number(res.ended || 0);
    /* Nothing else signed in is a good answer, not an empty one: it is the
       person being told the account is already as tight as they wanted it. */
    setOthersNote(n === 0
      ? 'Nothing else was signed in.'
      : `Done — ${n} other session${n === 1 ? '' : 's'} ended.`);
  };

  /* `username: true` — this sheet is the one screen that shows a person their
     own address, so it is the one caller that asks for it. Off everywhere else,
     mdSync above all: answering it costs the server a registry read, and mdSync
     calls the same route on every single sync. */
  useEt(() => { window.mdAuthStatus({ username: true }).then((s) => setAuth(mdAuthHeld(s))); }, []);

  /* mdAuthHeld on BOTH doors, and on the shared one deliberately: `refreshAuth`
     runs after signing in, after linking a device and after a rename, and a
     network blip on any of those would otherwise sign the account off the
     screen the moment it succeeded. See mdAuthHeld. */
  const refreshAuth = async () => {
    const s = mdAuthHeld(await window.mdAuthStatus({ username: true }));
    setAuth(s); onAuthChange && onAuthChange(s); return s;
  };

  const AUTH_ERRORS = {
    cancelled: null,                      // dismissing the system sheet is not an error
    unsupported: 'This device does not support passkeys.',
    /* Said for the device-link code, which is the only code Settings redeems
       now. The words are md-landing's, because it is the same refusal. */
    invalid_code: 'That code was not recognised, or has already been used.',
    counter_regression: 'This passkey looks like it has been copied. Signing in was refused.',
    unknown_credential: 'No account found for that passkey on this server.',
  };
  const say = (err) => (err in AUTH_ERRORS ? AUTH_ERRORS[err] : 'Something went wrong. Please try again.');

  const doSignIn = async () => {
    setBusy('signin'); setNote(null);
    const r = await window.mdSignInPasskey();
    setBusy(null);
    if (!r.ok) { setNote(say(r.error)); return; }
    await refreshAuth();
    const s = await window.mdSync({ force: true });
    setSyncNote(s.ok ? { pulled: s.pulled, pushed: s.pushed } : null);
  };

  /* The picture, fetched once and only when there is one to fetch. A refusal is
     ordinary — it is what a plane looks like — and the answer then is initials,
     not a spinner and not a broken image. */
  useEt(() => {
    let live = true;
    const hash = typeof window.mdPersonGet === 'function' ? window.mdPersonGet('picture') : null;
    if (!hash || typeof window.mdPersonPictureUrl !== 'function') { setFace(null); return undefined; }
    window.mdPersonPictureUrl().then((url) => { if (live) setFace(url || null); });
    return () => { live = false; };
  }, [bumped]);

  /* ONE guarded read, rather than a dozen bare calls. md-person.ts always
     ships — index.html loads it and scripts/verify-artifact.mjs proves it is
     there — but every module in this app is loadable on its own in the suite,
     and a sheet that threw because one of them was absent would take the
     whole of Settings down with it, not just a section. */
  const person = typeof window.mdPersonGet === 'function' ? {
    name: window.mdPersonGet('name'),
    gender: window.mdPersonGet('gender'),
    picture: window.mdPersonGet('picture'),
    dob: sPersonDob(),
    initials: window.mdPersonInitials(),
  } : null;

  /* One write for every profile edit, so every one of them is stamped, merged
     field-wise and on the other devices without this surface knowing how. */
  const setField = (field, value) => {
    if (!window.mdPersonSet(field, value)) { setNote('That could not be saved.'); return; }
    setEditing(null);
    if (window.mdSyncSoon) window.mdSyncSoon(0);
    bump((n) => n + 1);
  };

  const openField = (field) => {
    if (editing === field) { setEditing(null); return; }
    setNote(null);
    if (field === 'name') setDraftName((person && person.name) || '');
    if (field === 'dob') {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(window.mdPersonGet('dob') || '');
      setDraftDob(m ? { day: m[3], month: m[2], year: m[1] } : { day: '', month: '', year: '' });
    }
    setEditing(field);
  };

  const doPicture = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file || !window.mdPersonPicturePut) return;
    setBusy('picture'); setNote(null);
    const put = await window.mdPersonPicturePut(file);
    setBusy(null);
    if (!put.ok) { setNote('That picture could not be saved.'); return; }
    if (window.mdSyncSoon) window.mdSyncSoon(0);
    bump((n) => n + 1);
  };

  const doSignOut = async () => {
    setBusy('signout');
    await window.mdSignOut();
    window.mdResetSyncState();
    setBusy(null); setSyncNote(null); setSyncError(null);
    await refreshAuth();
    /* Signing out returns you to the front door, which is the other half of the
       rule that put the door there: signed out means the landing page, and a
       sign-out that left you sitting in the app would make that a rule about
       first visits rather than about sessions. Nothing local is deleted — the
       records on this device are still on this device. */
    onSignedOut && onSignedOut();
  };

  /* A KEY FOR THE DEVICE ALREADY HOLDING THE ROOM. Every door into this app
     used to mint a passkey on the way through, so a signed-in device was
     assumed to have one — and where that assumption failed there was no repair
     but to sign out first and hope a code was already in hand.

     There is no way to ask whether this device holds one (WebAuthn refuses to
     enumerate), so the row is always offered and the authenticator answers:
     `already` is InvalidStateError, which is the device saying it is set up. */
  const doAddPasskey = async () => {
    setBusy('passkey'); setPasskeyNote(null);
    const r = window.mdRegisterPasskey ? await window.mdRegisterPasskey() : null;
    setBusy(null);
    if (r && r.ok) { setPasskeyNote('Done — this device can sign you in on its own now.'); return; }
    setPasskeyNote(MD_ADD_PASSKEY_NOTE[(r && r.error) || 'failed'] || MD_ADD_PASSKEY_NOTE.failed);
  };

  /* The bare code is held, not a sentence about it: it is drawn large enough to
     read across a desk AND encoded into the QR beside it, and a display string
     could do neither. */
  const doStartLink = async () => {
    setBusy('linkstart'); setLinkNote(null);
    const r = await window.mdStartDeviceLink();
    setBusy(null);
    if (!r.ok) { setLinkCode(null); setLinkNote('Could not create a code.'); return; }
    setLinkCode(r.code);
  };

  /* The other direction: a screen that cannot scan shows a code and waits, and
     this is where somebody types it in. Nothing is minted here and nothing is
     redeemed — the one call hands this account's identity to whoever is holding
     that code, which is why the words above the field say so and why the press
     is separate from the typing. Refusals borrow the scanned surface's own
     sentences (app/md-pair.jsx): one wording, one thing to keep true. */
  const doGrantPair = async () => {
    const code = pairInput.trim();
    if (!code) return;
    setBusy('pairgrant'); setPairNote(null);
    const r = await window.mdPairGrant(code);
    setBusy(null);
    if (!r || !r.ok) {
      setPairNote(window.mdPairRefusalWords
        ? window.mdPairRefusalWords(r && r.error)
        : 'That did not go through, and nothing was approved.');
      return;
    }
    /* The field goes with the code. It was one-shot on the server the moment
       this returned, so leaving it filled would offer a second press that can
       only ever be refused. */
    setPairOpen(false); setPairInput('');
    setPairNote('That screen is signing in. It may take a few seconds to notice.');
  };

  const doUseLink = async () => {
    setBusy('link'); setNote(null);
    const r = await window.mdUseDeviceLink(linkInput.trim());
    setBusy(null);
    if (!r.ok) { setNote(say(r.error)); return; }
    setLinking(false); setLinkInput('');
    await refreshAuth();
    const s = await window.mdSync({ force: true });
    setSyncNote(s.ok ? { pulled: s.pulled, pushed: s.pushed } : null);
  };

  /* Deliberately two taps. The first arms it, the second does it — there is no
     undo and no grace period: the account's rows are deleted, not marked. The
     server keeps one thing and only one, the fact that this id was deleted, so
     the other devices can be TOLD rather than left waiting politely for a
     session that is never coming back.

     ── THE ORDER IS THE STORY ────────────────────────────────────────────────
     Every step is where it is because moving it breaks something concrete, and
     two of them were simply missing: mdLiveStop() was never called, so the
     socket stayed open to a room with no account behind it; nothing returned
     to the film, so the app kept running — signed in by appearances, holding
     every record locally. This row said "Erases everything on the server",
     which was true and misleading in the same sentence. */
  const doDelete = async () => {
    /* ARMED IN THE CONTROL ITSELF, not in a note beside it. The words used to
       appear above while the button went on saying "Delete my account", which
       left the screen holding one sentence telling you to press again and one
       control still labelled as though it had not been pressed. */
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setBusy('delete');

    /* 1. Tell the other devices FIRST, while this device still has an identity
          to speak with. Every frame says which device sent it, and step 5 takes
          that away — published afterwards this would be a frame from nobody,
          which the other devices drop at the door. Awaited only so it is on the
          wire before the socket closes; a failure is not a reason to stop,
          because the server's answer below is the durable one. */
    if (typeof window.mdLivePublish === 'function') {
      try { await window.mdLivePublish({ t: 'evict' }); } catch (e) { /* they will find out */ }
    }

    /* 2. The fact. Awaited, because everything below is a consequence of it —
          and a failure is still not a reason to stop: the account may already
          be gone, or the phone may be in a tunnel, and somebody who pressed
          Delete twice must not be left sitting in an app that looks signed in. */
    try {
      await fetch('/api/sync/account', { method: 'DELETE', credentials: 'same-origin' });
    } catch (e) {}

    /* 3. The sync cursors, before the room they live in goes. */
    window.mdResetSyncState();
    setBusy(null); setConfirmDelete(false); setNote(null); setSyncNote(null);

    /* 4. And out through the one door. mdGateEvicted (app/md-gate.ts) stops the
          socket, destroys the room and its databases, forgets both marks and
          reloads — and it is the SAME function the boot's gate, a sync and an
          evict frame all use. This row is the fourth way to learn an account
          is gone; four teardowns that were nearly the same would be four that
          drifted apart, and the one that drifted would be the one nobody was
          watching. */
    if (typeof window.mdGateEvicted === 'function') await window.mdGateEvicted();
    else onSignedOut && onSignedOut();
  };

  const doSyncNow = async () => {
    setBusy('sync'); setSyncNote(null); setSyncError(null);
    const s = await window.mdSync();
    setBusy(null);
    /* THE REFUSED LIST TRAVELS WITH THE COUNTS, and that is main's fix landing
       in this branch's grammar rather than being overwritten by it. `mdSync`
       names every document the server declined; both shapes this row has worn
       threw the list away, and here that is worse than it was — a refused
       document is still unsent, so the pending count never falls and the row
       promises for ever that a press will send it. `mdSyncSaid` decides what
       to make of it; this line's only job is not to lose it. */
    if (s.ok) setSyncNote({ pulled: s.pulled, pushed: s.pushed, refused: s.refused });
    else setSyncError('Could not sync just now. Nothing was lost — it will try again.');
    bump((n) => n + 1);
  };

  /* The word the user chose, not the theme it resolves to: this row shows a
     choice back to somebody, and "System" showing "Dark" would be the screen
     answering a question nobody asked. */
  const themeChoice = theme === 'light' || theme === 'system' ? theme : 'dark';

  /* How many documents this device is holding that the server has not
     acknowledged. mdSyncStates walks all of storage, so it is asked only when
     there is a row to answer for — signed in, with somewhere to sync to — and
     memoised against the things that can change the answer. */
  const pendingOut = useMt(() => {
    if (!auth || !auth.signedIn || !window.mdSyncStates) return 0;
    try {
      return Object.values(window.mdSyncStates()).filter((where) => where !== 'synced').length;
    } catch (e) { return 0; }
  }, [auth, bumped, syncNote, syncError]);
  const syncSaid = mdSyncSaid({ busy: busy === 'sync', error: syncError,
    told: syncNote, pending: pendingOut });

  const loc = window.mdLocation();
  const calc = window.mdCalc();
  const methods = window.MD_METHODS || [];
  const methodLabel = (methods.find((m) => m.id === calc.method) || {}).label || calc.method;

  /* THEME AND LANGUAGE STAND WITH THE ACCOUNT (Marwan, 2026-08-31): "Move the
     theme tab to be in the Account section under the Passkeys and Devices tab"
     and "Move the Language Tab to be in the account section right before the
     sign out tab". Two sections of one row each became two rows on a section
     that was already there — which is the same reading the whole phase has
     been making, one level up: a heading over a single row is a heading that
     has not earned itself.

     THEY ARE NOT ABOUT AN ACCOUNT, AND THEY STILL BELONG HERE. Account is now
     the section for how this app comes to you — who you are, how you get in,
     how it looks, what language it speaks, and the way out. So the rows are
     drawn whether or not anybody is signed in, above the sign-in rows'
     replacement rather than vanishing with them.

     `atBottom` is whether anything follows: signed in, Sign out does; signed
     out, nothing does, and the last thing drawn owes the card no rule. */
  const appearanceRows = (atBottom) => (<>
    <SRow label="Theme" lead={MDIS.contrast(GLYPH(), 17)}
      right={S_THEME_WORD[themeChoice] || S_THEME_WORD.dark}
      accent={accent} onClick={() => openField('theme')} />
    {editing === 'theme' && (
      <div data-md-theme-options="">
        {S_THEMES.map(([id, word, why]) => (
          <SRow key={id} label={word} sub={why} lead
            right={themeChoice === id ? CHECK(GLYPH()) : null}
            accent={accent}
            onClick={() => { onTheme && onTheme(id); setEditing(null); }} />
        ))}
      </div>
    )}
    {/* THE LABEL IS THE QUESTION AND THE ANSWER IS BESIDE IT (Marwan,
        2026-08-31): "Change the word English to Language and then put En at
        the right end of the card." The row read "English" over "Left to
        right", which is a row saying its answer twice and never saying what
        it was the answer TO. */}
    <SRow label="Language" lead={MDIS.globe(GLYPH(), 17)}
      right={window.mdLang() === 'ar' ? 'ع' : 'En'}
      accent={accent} last={atBottom && editing !== 'language'}
      onClick={() => openField('language')} />
    {editing === 'language' && (
      <div data-md-language-options="">
        <SRow label="English" sub="Left to right" lead
          right={window.mdLang() === 'en' ? CHECK(GLYPH()) : null} accent={accent}
          onClick={() => { window.mdSetLang('en'); setEditing(null); bump((n) => n + 1); }} />
        <SRow label="العربية" sub="Right to left — prayer names only for now" lead
          right={window.mdLang() === 'ar' ? CHECK(GLYPH()) : null} accent={accent}
          last={atBottom}
          onClick={() => { window.mdSetLang('ar'); setEditing(null); bump((n) => n + 1); }} />
      </div>
    )}
  </>);

  const apply = (l) => { onLocation(l); bump((n) => n + 1); };

  /* ── the three questions, as a dialog ──────────────────────────────────────
     ROUND FOUR (Marwan, 2026-08-31): false is shut; `true` is open with the
     lists at rest; 'country' is open with the country question already asked
     and its search focused — the by-hand gesture, where the person has said
     they want to choose. A deep link that used to name the location screen
     seeds the dialog open, lists at rest, because the link promised the place
     and not a keyboard grab. The open-on-its-own welcome for a person with no
     location died with the move: a modal must not open itself under somebody
     who came for the theme — the row says the question is open instead. */
  const arrivedAtPlace = initialView === 'place' || initialView === 'city';
  const [placeAsk, setPlaceAsk] = useSt(arrivedAtPlace ? true : false);
  /* A link can arrive at a sheet ALREADY STANDING — Settings restored on
     reload, then the place link pressed — and it reaches here as a prop
     change, not a mount. The transition is the ask; a dialog the person then
     closes stays closed, because the dep does not move again. */
  useEt(() => { if (arrivedAtPlace) setPlaceAsk((p) => p || true); }, [arrivedAtPlace]);

  /* ── the allow-location walkthrough ────────────────────────────────────────
     Plain data on `locAsk` — { stage, wait?, why?, city?, moved? } — rendered
     by LocateAsk with mdLocateSaid's words. The crosshair does NOT fire the
     browser's permission prompt: it opens a sentence first, and the browser's
     dialog is only ever the second half of a conversation the person already
     agreed to.

     THE PRE-CHECKS KEEP THE SENTENCE TRUE. A browser with no geolocation gets
     the cannot-ask ending without a doomed Allow button; a site the browser
     has already blocked opens on the remedy, because pressing Allow there
     fails without the browser ever asking; and a permission already granted
     skips the question — "your browser will ask once" would be false. A
     browser that will not say (Safari has no Permissions API for geolocation)
     is treated as a browser about to ask, same as mdPreciseAsk itself. */
  const [locAsk, setLocAsk] = useSt(null);
  const cityTable = useCityTableS(!!placeAsk || !!locAsk);

  const runDetect = async (wait) => {
    setLocAsk({ stage: 'busy', wait });
    setBusy('precise');
    /* The gazetteer rides the wait: fetched (or recalled) while the radio
       looks, so the fix has a name the moment it lands. mdWorldCities never
       rejects — offline it answers with the boot table. */
    const tableSoon = window.mdWorldCities ? window.mdWorldCities() : Promise.resolve(null);
    const said = await window.mdPreciseAsk();
    setBusy(null);
    if (!said.place) { setLocAsk({ stage: 'failed', why: said.why }); return; }
    /* THE FIX KEEPS ITS COORDINATES AND TAKES A NAME. The nearest row of the
       gazetteer names the place — city, region, country, time zone — and the
       device's own lat/lon stay for the prayer maths, because a centroid is
       never more precise than the fix itself. */
    const near = window.mdNearestCity
      ? window.mdNearestCity((await tableSoon) || cityTable, said.place.lat, said.place.lon)
      : null;
    const place = near
      ? { ...said.place, city: near.city, region: near.region || null,
          country: near.country || null,
          timezone: near.timezone || said.place.timezone || null }
      : said.place;
    const before = window.mdCalc().method;
    apply(place);
    const after = window.mdCalc().method;
    const moved = after !== before
      ? ((methods.find((m) => m.id === after) || {}).label || after) : null;
    setLocAsk({ stage: 'found', city: window.mdLocationLabel(place), moved });
  };

  const askDetect = async () => {
    if (!navigator.geolocation) { setLocAsk({ stage: 'failed', why: 'unsupported' }); return; }
    const perm = await window.mdGeoPermission();
    if (perm === 'denied') { setLocAsk({ stage: 'failed', why: 'blocked' }); return; }
    if (perm === 'granted') { runDetect(false); return; }
    setLocAsk({ stage: 'ask' });
  };

  return (
    <>
    {/* Mounted unconditionally, like every other confirmation in this app: it
        holds its own dismiss layer, which has to be armed and disarmed in the
        same order on every render. With no device pressed it draws nothing. */}
    <DeviceRemoveAsk device={removing} note={removeNote} busy={busy === 'devremove'}
      accent={accent} onConfirm={doRemove}
      onCancel={() => { setRemoving(null); setRemoveNote(null); }} />
    <SignOutOthersAsk open={othersAsk} note={othersAsk ? othersNote : null}
      busy={busy === 'others'} accent={accent} onConfirm={doSignOutOthers}
      onCancel={() => { setOthersAsk(false); setOthersNote(null); }} />
    <LocateAsk ask={locAsk} accent={accent}
      onAllow={() => runDetect(true)}
      onPick={() => { setLocAsk(null); setPlaceAsk('country'); }}
      onClose={() => setLocAsk(null)} />
    {/* `settled` is the picker saying the walk is over — a city, or a
        division that IS its one city. A country or division pick applies and
        leaves the dialog standing for the next question. */}
    <PlaceAsk open={!!placeAsk} accent={accent} loc={loc} table={cityTable}
      startOpen={placeAsk === 'country'}
      onPick={(c, settled) => { apply(c); if (settled !== false) setPlaceAsk(false); }}
      onClose={() => setPlaceAsk(false)} />
    <div data-md-panel-screen="atomic"
      data-md-phone-document-screen={phoneDocument ? 'true' : undefined}
      style={{ position: 'absolute', inset: 0, zIndex: 90, display: 'flex', flexDirection: 'column',
      ...(window.mdFrostDress ? window.mdFrostDress(light) : {}),
      borderRadius: 'var(--md-radius-sheet) var(--md-radius-sheet) 0 0' }}>

      {/* header — this sheet runs flush to the top edge of the screen, so on a
          phone the safe area has to be spent here: a flat 16px left the title
          and the close button underneath the status bar, where the sheet could
          not be dismissed. The token is the one the hero and the board toolbar
          already agree on; +2 is the nudge the hero's own edge carries, and on
          a desktop (inset 0) the sum is the 16px this used to be. */}
      {/* Every screen of this sheet is titled BY the sheet (Polish 5): the
          sub-page's own name and icon stand where "Settings" stands at the
          root, and the dismissal beside them steps back exactly one level —
          the navigational pattern the rest of the app follows. No back button:
          that one control IS the way back, and only at the root does it close
          the sheet. It is the app's shared mark since polish 6 (md-bar.jsx),
          so a sub-screen here is put away with the same gesture as any other
          screen — the LABEL is what says whether it steps back or closes. */}
      {(() => {
        const flowOpen = view === 'workflow' && window.mdWorkflowById
          ? window.mdWorkflowById(frames.frame.flowId) : null;
        const screen = view === 'workflow'
          ? [flowOpen ? flowOpen.name : 'Workflows', 'workflow']
          : (S_SCREEN[view] || S_SCREEN.root);
        /* THE WAY BACK NAMES WHERE IT GOES, and it reads the frame underneath
            rather than assuming there is only one. Trash reached from Manage
            your data steps back to Manage, not to Settings — a label that said
            "Back to Settings" and then landed somewhere else would be the sheet
            lying about its own shape. A deep link seeds the walk with one
            frame, so its way out says Settings, which is where it goes. */
        const under = frames.stack.length > 1 ? frames.stack[frames.stack.length - 2] : null;
        const beneath = under
          ? (under.id === 'workflow' ? 'Workflows' : (S_SCREEN[under.id] || S_SCREEN.root)[0])
          : 'Settings';
        return <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 'calc(var(--md-top-inset) + 2px) 18px 4px', flexShrink: 0 }}>
          <span data-md-title-icon={view === 'root' ? 'settings' : view}
            style={{ display: 'grid', placeItems: 'center', flex: 'none' }}>
            {(MDIS[screen[1]] || (() => null))(rgbaS(accent, 1), 22)}
          </span>
          <span data-md-sheet-title style={{ flex: 1, color: inkS(0.95), fontSize: 19, fontWeight: 700 }}>
            {screen[0]}
          </span>
          <window.MdDismiss onClick={view === 'root' ? onClose : stepBack}
            label={view === 'root' ? 'Close settings' : `Back to ${beneath}`}
            style={{ marginInlineEnd: -10 }} />
        </div>;
      })()}

      {/* scroll padding at the bottom spends the same inset from the other end,
          so the last row clears the home indicator instead of sliding under it;
          inset 0 leaves the original 28px. */}
      <div data-md-phone-document-content={phoneDocument ? 'true' : undefined}
        className="md-scroll" style={{ flex: 1, overflowY: 'auto', padding: '4px 18px calc(var(--md-bottom-inset) + 14px)' }}>
        {/* Workflows lives in its own file (md-workflows.jsx) and is reached
            through window at RENDER time, not destructured at module load:
            this file is evaluated before it. See its header for why the page
            is a guest in a surface that calls itself temporary. The page is
            CONTROLLED: this sheet says which flow stands (the 'workflow'
            frame), and a pressed row asks through onOpen. */}
        {(view === 'workflows' || view === 'workflow') && window.WorkflowsPage && (
          <window.WorkflowsPage accent={accent} onOpen={openFlow}
            flowId={view === 'workflow' ? frames.frame.flowId : null} />
        )}

        {view === 'trash' && <TrashViewS accent={accent} />}

        {view === 'method' && (
          <>
            <p style={{ color: inkS(0.45), fontSize: 12.5, lineHeight: 1.5, margin: '0 4px 12px' }}>
              Authorities use different solar angles for Fajr and Isha. This is a difference of
              scholarly opinion, not accuracy — choose the one your community follows.
            </p>
            <GlassS radius={18} alpha={0.08} style={{ overflow: 'hidden' }}>
              {methods.map((m, i) => (
                <SRow key={m.id} label={m.label} sub={m.note} accent={accent} last={i === methods.length - 1}
                  right={calc.method === m.id ? CHECK(GLYPH()) : null}
                  onClick={() => { onCalc({ ...calc, method: m.id }); bump((n) => n + 1); }} />
              ))}
            </GlassS>
            {/* ASR STANDS WHERE ITS EFFECT IS VISIBLE. It was on the root, two
                screens away from the only thing it changes; here the Asr time
                in the list below moves as it is pressed, which is the whole
                explanation and costs no words. */}
            {/* OFFERED THE WAY THE TWELVE ABOVE IT ARE. It arrived here as a
                toggle with "Earlier" or "Later" in the right-hand slot — the
                slot that says "System" on Theme and "English" on Language, and
                so reads as the answer rather than as a remark about the clock.
                Two rows and a check is the pattern this screen already teaches
                three inches higher, and it leaves nothing to interpret.

                Neither opinion is a default and neither is more correct, so
                they carry the same weight and the words describe the shadow
                rather than recommending one. Which is earlier is a question the
                Today list underneath answers honestly, in times. */}
            <SSection label="Asr">
              {ASR.map(([id, word, shadow], i) => (
                <SRow key={id} label={word} sub={shadow} accent={accent} last={i === ASR.length - 1}
                  right={calc.madhab === id ? CHECK(GLYPH()) : null}
                  onClick={() => { onCalc({ ...calc, madhab: id }); bump((n) => n + 1); }} />
              ))}
            </SSection>
            <SSection label="Today">
              {window.mdPrayersFor(0).map((p, i, arr) => (
                <SRow key={p.id} label={p.name} sub={p.desc} right={window.mdFmt(p.t)}
                  last={i === arr.length - 1} />
              ))}
            </SSection>
          </>
        )}

        {/* ── MANAGE YOUR DATA ───────────────────────────────────────────────
            The verbs and the full reading, one screen in from the two bars on
            the root. Deliberately OUTSIDE the Account section on the way here:
            a backup that needs an account is not a backup, and every one of
            these works signed out, offline, and without the server having ever
            seen a byte of it. */}
        {view === 'data' && (
          <>
            <SSection label="Keep a copy">
              <SRow label="Back up to a file" sub="Everything on this device. No account needed."
                accent={accent}
                onClick={async () => { await window.mdDownloadBackup(); setDataNote('Backup downloaded'); }} />
              <SRow label="Restore from a file" sub={dataNote || 'Replaces what is on this device'}
                accent={accent} last
                onClick={() => fileRef.current && fileRef.current.click()} />
            </SSection>

            {/* Hidden input rather than a styled control: the file picker has to
                be opened by a real <input type="file"> click to satisfy every
                browser's user-gesture rule. It travels with the two rows that
                use it. */}
            <input ref={fileRef} type="file" accept="application/json,.json"
              style={{ display: 'none' }} aria-hidden="true" tabIndex={-1}
              onChange={async (e) => {
                const file = e.target.files && e.target.files[0];
                e.target.value = '';                      // let the same file be picked twice
                if (!file) return;
                const r = await window.mdImportFromFile(file);
                setDataNote(r.ok ? `Restored ${r.restored} items — reopening…` : r.error);
                // A restore rewrites every store underneath a running app, so
                // the only honest thing to do is start again from disk.
                if (r.ok) setTimeout(() => window.location.reload(), 900);
              }} />

            <SSection label="Deleted">
              {/* PUSHES, so the way back lands on this screen and not on the
                  root — Trash is inside Manage now, and Back has to agree. */}
              <SRow label="Trash"
                sub={trashCount
                  ? `${trashCount} ${trashCount === 1 ? 'deletion' : 'deletions'} · Up to 30 days to restore`
                  : 'Nothing in Trash'}
                right={MDIS.chevron(GLYPH(), 9)} accent={accent} last
                onClick={() => pushView('trash')} />
            </SSection>

            {/* The reading, in full: the same two bars the root shows, then what
                is taking the room, what is still uploading, whether this browser
                has promised to keep any of it, and the last save that failed. */}
            <SSection label="What is taking room">
              <StorageWidget accent={accent} />
            </SSection>

            <p style={{ color: inkS(0.32), fontSize: 11.5, lineHeight: 1.6, margin: '18px 4px 0' }}>
              A backup file is yours: it is written by this device, saved where you say, and never
              sent anywhere. Restoring one replaces what is on this device with what is in it.
            </p>
          </>
        )}

        {/* ── YOUR PROFILE ───────────────────────────────────────────────────
            The three answers given at the door, afterwards. Every edit goes
            through mdPersonSet — so every one is stamped, merged field by field,
            and on the other devices without this surface knowing how a profile
            is stored. */}
        {view === 'profile' && auth && auth.signedIn && person && (
          <>
            <SSection label="You">
              <SRow
                label={person.name || 'Your name'}
                sub="What ManDana calls you"
                right={<SFace url={face} initials={person.initials} />}
                accent={accent} onClick={() => openField('name')} />
              {editing === 'name' && (
                /* autoFocus and Enter, and NO local Escape handler — the app
                   has one Escape law (md-dismiss.jsx) and a second one that
                   only this field obeyed would be the bug. The row is the
                   toggle; tapping it again closes the field. Same shape a
                   device is renamed with, one screen over. */
                <div style={{ padding: '0 16px 14px' }}>
                  <input value={draftName} onChange={(e) => setDraftName(e.target.value)}
                    autoFocus spellCheck={false} aria-label="Your name"
                    onKeyDown={(e) => { if (e.key === 'Enter') setField('name', draftName.trim()); }}
                    style={SFIELD(light)} />
                </div>
              )}

              {/* The other half of the row above it. The name is what ManDana
                  calls you; this is what everybody else types to reach you,
                  and this sheet is the only place in the app it can be read
                  after the moment it was handed over.

                  NOT A ROW, DELIBERATELY. Every SRow here is a button, and a
                  button is a promise that pressing it does something — but a
                  username is allocated by the registry and permanent, so
                  there is nothing to open. It is drawn to the same measure as
                  its neighbours and is simply not a control.

                  `userSelect: 'text'` is then stated rather than assumed,
                  because the address has to be copyable — it carries dots and
                  a domain, and copying it by eye is how a person mistypes it.
                  (A disabled button was measured, in Chromium, and its text
                  IS selectable once user-select says so — so that is not the
                  reason this is a div. The reason is the promise above.)

                  Absent, not blank, when there is no username: every account
                  made before the invitation gate has no registry row, and a
                  "Not set" here would read as an answer somebody still owes
                  when nobody can set it. */}
              {auth && auth.username && (
                <div data-md-settings-username
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px',
                    borderBottom: `1px solid ${inkS(light ? 0.07 : 0.06)}` }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {/* Broken at the @, which is the seam an address has.
                        Left to break wherever the line happened to fill, a
                        long username split the digit off its own number —
                        "…elsayedgalaly.1 / 2@omana.network" — which reads as
                        a different address than the one it is. `break-word`
                        rather than `break-all` so the <wbr> below is the
                        break the browser reaches for first, with an
                        arbitrary one still available for a local part too
                        long to fit on a line by itself. */}
                    <span data-md-settings-username-value
                      style={{ display: 'block', color: inkS(0.92), fontSize: 14.5, fontWeight: 600,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        letterSpacing: '0.01em', overflowWrap: 'break-word', userSelect: 'text' }}>
                      {sUsernameParts(auth.username).map((part, i) => (
                        i === 0
                          ? <React.Fragment key="local">{part}</React.Fragment>
                          : <React.Fragment key="domain"><wbr />{part}</React.Fragment>
                      ))}
                    </span>
                    <span style={{ display: 'block', color: inkS(0.45), fontSize: 12.5, marginTop: 2 }}>
                      What other people type to reach you
                    </span>
                  </span>
                </div>
              )}

              <SRow label="Date of birth" sub={person.dob || 'Not set'}
                accent={accent} onClick={() => openField('dob')} />
              {editing === 'dob' && (
                <SDobField value={draftDob} light={light} accent={accent}
                  onChange={setDraftDob}
                  onSave={() => setField('dob', sDobKey(draftDob))} />
              )}

              <SRow label="Gender" sub={sWord(person.gender) || 'Not set'}
                accent={accent} onClick={() => openField('gender')} last={editing !== 'gender'} />
              {editing === 'gender' && (
                <div role="radiogroup" aria-label="Gender"
                  style={{ display: 'flex', gap: 8, padding: '0 16px 14px' }}>
                  {(window.MD_PERSON_GENDERS || []).map((word) => {
                    const on = person.gender === word;
                    return (
                      <button key={word} type="button" role="radio" aria-checked={on ? 'true' : 'false'}
                        onClick={() => setField('gender', word)}
                        style={{ flex: 1, padding: '11px 8px', borderRadius: 12, fontSize: 15,
                          border: `1px solid ${inkS(on ? 0.22 : 0.08)}`,
                          background: on ? rgbaS(accent, 0.16) : surfS(0.10),
                          color: inkS(on ? 0.92 : 0.6), cursor: 'pointer' }}>
                        {sWord(word)}
                      </button>);
                  })}
                </div>
              )}

              <SRow label="Picture" sub={person.picture ? 'Set' : 'Optional'}
                right={busy === 'picture' ? '…' : null} accent={accent} last
                onClick={() => { const el = document.getElementById('md-settings-pic'); if (el) el.click(); }} />
              <input id="md-settings-pic" type="file" accept="image/*" onChange={doPicture}
                style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />
            </SSection>

            {note && (
              <p role="status" style={{ color: rgbaS('#FFB3C7', 0.9), fontSize: 12.5, lineHeight: 1.5,
                margin: '12px 4px 0' }}>{note}</p>
            )}

            {/* ── the last thing on the screen ────────────────────────────
                NOT A ROW, and that is the whole point of where it is. A red
                row in a list is a thing a finger can land on while scrolling
                past; this is at the bottom of the deepest screen a person
                reaches on purpose, drawn as a sentence, and it still takes two
                presses. Nothing about the teardown changed — doDelete is the
                same five steps it has always been. */}
            <div style={{ margin: '26px 4px 6px', paddingTop: 18,
              borderTop: `1px solid ${inkS(light ? 0.07 : 0.06)}` }}>
              <p style={{ color: inkS(0.32), fontSize: 11.5, lineHeight: 1.6, margin: '0 0 8px' }}>
                Deleting your account removes it from the server and from every device it is
                signed in on. It cannot be undone.
              </p>
              <button type="button" data-md-delete-account="" onClick={busy ? null : doDelete}
                disabled={!!busy}
                style={{ background: 'transparent', border: 'none', padding: '2px 0', cursor: busy ? 'default' : 'pointer',
                  color: rgbaS('#FF5C74', 0.96), fontSize: 13.5, fontWeight: 600,
                  textDecoration: 'underline', textUnderlineOffset: 3 }}>
                {busy === 'delete' ? 'Deleting…' : confirmDelete ? 'Tap again to delete my account' : 'Delete my account'}
              </button>
            </div>
          </>
        )}

        {/* A LINK CAN OUTLIVE A SESSION. Both screens below need an account,
            and a person who follows an old link after signing out would
            otherwise land on a titled screen holding nothing at all. Drawn only
            once auth has actually answered — `auth` is null until then, and
            flashing "signed out" at somebody who is signed in is the one thing
            worse than waiting a moment. */}
        {(view === 'devices' || view === 'profile') && auth && !auth.signedIn && (
          <p style={{ color: inkS(0.45), fontSize: 13.5, lineHeight: 1.6, margin: '8px 4px 0' }}>
            Sign in to see this. Everything already on this device stays where it is.
          </p>
        )}

        {/* ── PASSKEYS & DEVICES ─────────────────────────────────────────────
            Every way into this account, on one screen, grouped as the three
            questions a person actually has: can this device let me in on its
            own, how do I let another one in, and who is already in. Every verb,
            every refusal string and every confirmation is called unchanged from
            its new home. */}
        {view === 'devices' && auth && auth.signedIn && (
          <>
            <SSection label="This device">
              {/* THIS DEVICE FIRST. The rows below are about other screens;
                  this one is about the screen being held, and a person who
                  needs it needs it before anything else on this list. */}
              <SRow label="Add a passkey to this device"
                sub={passkeyNote || 'Sign in with Face ID or your PIN next time, without a code'}
                right={busy === 'passkey' ? '…' : null} accent={accent} last
                onClick={busy ? null : doAddPasskey} />
            </SSection>

            <SSection label="Another device">
              <SRow label="Add another device"
                sub={linkNote || (linkCode ? 'Scan it on the other device, or type it' : 'Get a code to sign in elsewhere')}
                right={busy === 'linkstart' ? '…' : null} accent={accent}
                last={!window.MD_PAIR_WARNING && !linkCode}
                onClick={busy ? null : doStartLink} />
              {linkCode && <DeviceLinkCode code={linkCode} light={light} />}
              {/* The reverse: a screen that cannot scan shows a code, and this
                  is the door for typing it in — over the telephone, or across
                  a room, or from a laptop with no camera at all.

                  THE WARNING IS THE GATE, so the door is not offered without
                  it. app/md-pair.jsx owns those words and is loaded before
                  this file; if it ever is not, the honest answer is no row at
                  all rather than a row that asks for a code and says nothing
                  about what typing one in does. `pairOpen` is unreachable
                  without this row, so guarding it here guards the field too. */}
              {window.MD_PAIR_WARNING && <SRow label="Sign in another screen"
                sub={pairNote || (pairOpen ? 'Type the code it is showing' : 'Approve a screen that is waiting with a code')}
                right={busy === 'pairgrant' ? '…' : null} accent={accent} last={!pairOpen}
                onClick={busy ? null : () => {
                  setPairNote(null); setPairInput('');
                  setPairOpen(!pairOpen);
                }} />}
              {pairOpen && (
                <div style={{ padding: '4px 16px 16px', background: inkS(0.03) }}>
                  <p style={{ margin: '0 0 12px', color: inkS(0.72), fontSize: 12.5, lineHeight: 1.55 }}>
                    {window.MD_PAIR_WARNING}
                  </p>
                  <input value={pairInput} onChange={(e) => setPairInput(e.target.value)}
                    data-md-pair-code-input="" autoFocus spellCheck={false}
                    autoComplete="one-time-code" aria-label="The code the other screen is showing"
                    placeholder="XXXX-XXXX"
                    onKeyDown={(e) => { if (e.key === 'Enter') doGrantPair(); }}
                    style={{ width: '100%', padding: '11px 14px', borderRadius: 12, fontSize: 15,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      letterSpacing: '0.1em', background: surfS(0.10), color: inkS(0.92),
                      border: `1px solid ${inkS(light ? 0.10 : 0.08)}`, outline: 'none' }} />
                  <button onClick={doGrantPair} disabled={!pairInput.trim() || !!busy}
                    style={{ width: '100%', marginTop: 8, padding: 12, borderRadius: 12, border: 'none',
                      cursor: pairInput.trim() ? 'pointer' : 'default',
                      background: pairInput.trim() ? rgbaS(accent, 1) : inkS(0.10),
                      color: pairInput.trim() ? '#fff' : inkS(0.3), fontSize: 15, fontWeight: 650 }}>
                    {busy === 'pairgrant' ? 'Approving…' : 'Approve'}
                  </button>
                </div>
              )}
            </SSection>

            {devices.length > 0 && (
              <SSection label="Signed in">
                {devices.map((d, i) => (
                  <SRow key={d.id} label={d.name}
                    sub={d.self ? 'This device'
                      : !d.live ? 'Not here right now'
                      : d.activeDesk && d.activeDesk === deskId ? 'On this desk'
                      : d.activeDesk ? 'On another desk'
                      : 'Online elsewhere'}
                    right={d.live ? <LiveMark accent={accent} self={d.self} /> : null}
                    accent={accent} last={i === devices.length - 1}
                    deviceName={d.name} reserveRemove={devices.length > 1}
                    /* NOT ON THIS DEVICE. Signing this one out is Sign out, on
                       the root, and an × here that did the same thing would be
                       a second door to the same room — one of which would
                       eventually describe itself differently. */
                    onRemove={d.self ? null : () => {
                      setRemoveNote(null);
                      setRemoving({ id: d.id, name: d.name });
                    }}
                    onClick={() => {
                      const open = renaming === d.id;
                      setRenaming(open ? null : d.id);
                      setRenameInput(open ? '' : d.name);
                    }} />
                ))}
              </SSection>
            )}

            {renaming && (
              <div style={{ marginTop: 10 }}>
                {/* autoFocus and Enter, because the row was tapped in order to
                    rename it — there is nothing else this field could be for,
                    and a rename that costs a second tap to begin and a reach
                    for a button to finish is the friction the list exists to
                    remove. The same shape md-board.jsx renames a board with.

                    NO ESCAPE HANDLER, deliberately. The app has ONE Escape law
                    (app/md-dismiss.jsx): with a layer standing, the first press
                    blurs the field and the second closes the surface, and the
                    arbiter silences every listener behind it. A local exception
                    here would be a second law that only this field obeys. The
                    row is the toggle; tapping it again closes the field. */}
                <input value={renameInput} onChange={(e) => setRenameInput(e.target.value)}
                  autoFocus data-md-device-name="" spellCheck={false}
                  aria-label={`Rename ${devices.find((d) => d.id === renaming)?.name || 'this device'}`}
                  onKeyDown={(e) => { if (e.key === 'Enter') doRename(); }}
                  placeholder="What to call this device"
                  style={{ width: '100%', padding: '11px 14px', borderRadius: 12, fontSize: 15,
                    background: surfS(0.10), color: inkS(0.92), border: `1px solid ${inkS(light ? 0.10 : 0.08)}`,
                    outline: 'none' }} />
                <button onClick={doRename} disabled={!renameInput.trim()}
                  style={{ width: '100%', marginTop: 8, padding: 12, borderRadius: 12, border: 'none',
                    cursor: renameInput.trim() ? 'pointer' : 'default',
                    background: renameInput.trim() ? rgbaS(accent, 1) : inkS(0.10),
                    color: renameInput.trim() ? '#fff' : inkS(0.3), fontSize: 15, fontWeight: 650 }}>
                  Rename
                </button>
              </div>
            )}

            <SSection label="Elsewhere">
              {/* Beside Sign out, aimed the other way — and on this screen
                  rather than beside it, because this one is about the devices
                  listed above and Sign out is about the one being held. */}
              <SRow label="Sign out everywhere else"
                sub={othersNote || 'Ends every session but this one'}
                right={busy === 'others' ? '…' : null} accent={accent} last
                onClick={busy ? null : () => { setOthersNote(null); setOthersAsk(true); }} />
            </SSection>
          </>
        )}

        {view === 'root' && (
          <>
            {/* Where things stand, and the words for it. Above Appearance
                because it is about the user's own life rather than about how
                the app looks at it. */}
            <SSection label="Organising">
              <SRow label="Workflows" lead={MDIS.workflow(GLYPH(), 17)}
                accent={accent} last
                onClick={() => goView('workflows')} />
            </SSection>

            {/* ── Prayer times and Hijri date ──────────────────────────────
                ONE SECTION FOR THE WHOLE OF THE DAY'S RECKONING (Marwan,
                2026-08-31): where you are, by whose reckoning the times are
                computed, and what the day is called. Location had a section to
                itself until 2026-08-30 and the Hijri date one until the day
                after — each a single row under its own all-caps heading, each
                answering a part of the same question.

                THE ORDER IS THE READING ORDER. A prayer time is a latitude, a
                longitude and an authority's angle, so the place comes first and
                the method second; the calendar names the day those times fall
                on, so it comes last. */}
            <SSection label="Prayer times and Hijri date">
              {/* THE ROW STARTS THE WALKTHROUGH (Marwan, 2026-08-31), exactly
                  as the crosshair does: a place is something the device
                  usually knows, so the deliberate press asks for it — and the
                  Where-are-you dialog waits behind the ask's quiet "Choose a
                  city", for whoever would rather answer by hand.

                  THE CROSSHAIR NEVER DISAPPEARS (Marwan, 2026-08-31). It is the
                  door to BOTH endings — a fix, or the country steps when the
                  device cannot give one — so it cannot be a control that only
                  exists while the app is guessing. It wore muted ink and
                  swapped for a "Change" button once a place was decided; the
                  swap made the door vanish exactly when somebody had used it,
                  and the muted ink made the app's own verb read as decoration.
                  One control, always here, in the accent — the row is its
                  bigger target now, not a different verb. */}
              {/* THE PIN AND THE CROSSHAIR ARE NOT THE SAME MARK, and this is
                  the row where the two ends of a row are easiest to see. The
                  pin on the left is the subject — where you are. The crosshair
                  on the right is a control — ask this device now. */}
              <SRow label={window.mdLocationLabel(loc)}
                accent={accent} lead={MDIS.loc(GLYPH(), 17)}
                onClick={askDetect}
                aside={{ glyph: MDIS.crosshair(GLYPH(), 17), accented: true,
                  busy: busy === 'precise',
                  label: "Use this device's location", attr: { 'data-md-use-precise': '' },
                  onClick: busy ? null : askDetect }} />
              {/* AND THIS ONE IS AN ANSWER NOBODY IS SCANNING FOR (Marwan,
                  2026-09-01). Round eight moved the method across rather than
                  striking it, on the rule that a list you cannot read your own
                  settings off is a list you have to open every row of. That
                  rule earns Dark and En, which are one word each and are taken
                  in at a glance. It does not earn "Egyptian General
                  Authority": the answer is longer than the question, it is
                  decided once and then never again, and on a phone it crowded
                  the row it was meant to annotate. It is on the screen this
                  row opens, beside the eleven it was chosen over. */}
              <SRow label="Calculation method" accent={accent}
                lead={MDIS.compass(GLYPH(), 17)}
                onClick={() => goView('method')} />
              {/* THE ROW IS THE DATE, and that is the whole of it (Marwan,
                  2026-08-31). It carried "Umm al-Qura calendar" underneath,
                  which named the authority — true, and answering a question
                  nobody standing here was asking. The authority is on the
                  screen this row opens.

                  A pencil sat at the right end until round eleven, arguing
                  that this is the one row on the list where pressing opens an
                  EDITOR rather than a screen. One bit of information, and the
                  price of it was a mark to scan past on the way down. Marwan
                  took the price rather than the bit. */}
              <SRow label={window.mdHijri(0)}
                lead={MDIS.calendar(GLYPH(), 17)}
                accent={accent} last={editing !== 'hijri'}
                onClick={() => openField('hijri')} />
              {editing === 'hijri' && (
                <div data-md-hijri-adjust=""
                  style={{ display: 'flex', alignItems: 'center', gap: 12,
                    padding: `13px 16px 13px ${16 + S_LEAD + 12}px` }}>
                  <span style={{ flex: 1, minWidth: 0, color: inkS(0.5), fontSize: 12.5, lineHeight: 1.5 }}>
                    Shift the date if your sighting differs
                  </span>
                  <HijriAdjuster accent={accent} value={window.mdHijriAdjust()}
                    onChange={(v) => { onHijriAdjust(v); bump((n) => n + 1); }} />
                </div>
              )}
            </SSection>

            {note && (
              <p role="status" style={{ color: rgbaS('#FFB3C7', 0.9), fontSize: 12.5, lineHeight: 1.5, margin: '10px 4px 0' }}>
                {note}
              </p>
            )}

            {/* ── Your data ────────────────────────────────────────────────
                A glance and a door. Both bars stay here because "am I running
                out of room?" is worth answering without a tap; everything a
                person might DO about it is one screen in. */}
            <SSection label="Your data">
              {/* SIGNED IN IS THE WHOLE TEST. It used to want a SECOND device
                  as well, on the reasoning that a lone device had nobody to be
                  out of step with. It has the server, it pushes everything it
                  holds, and the person most likely to want this row is the one
                  about to close the only laptop they own (Marwan, 2026-08-30).

                  The row reads its own state and is a control only when there
                  is something for the control to do — see mdSyncSaid. The glyph
                  carries the state on the attribute the stylesheet turns it by,
                  so the motion is declared once and is the desk's own. */}
              {/* A REFUSAL IS NEWS, NOT A CAPTION. Every other sentence under a
                  root row described the row and came off; this one is the
                  server's own reason the last press did not work, and struck it
                  the row would read "Sync now" with nothing at all to say a sync
                  had just failed. It speaks in that one state — settled and
                  waiting both say themselves in the label. */}
              {auth?.signedIn && (
                <SRow label={syncSaid.label} accent={accent}
                  sub={syncSaid.tone === 'refused' ? syncSaid.sub : null}
                  onClick={syncSaid.can && !busy ? doSyncNow : null}
                  lead={(
                    <span data-md-sync-state={syncSaid.tone}
                      aria-busy={syncSaid.tone === 'working' ? 'true' : undefined}
                      style={{ display: 'inline-flex', alignItems: 'center' }}>
                      {/* AT REST IT IS EXACTLY AS QUIET AS THE SENTENCE BESIDE
                          IT, and no quieter. 0.3 ink on a light card was a
                          ghost — fainter than its own caption, which is the
                          weight at which a control stops reading as one and
                          starts reading as decoration. */}
                      {MDIS.reset(syncSaid.can ? GLYPH() : inkS(0.45), 17)}
                    </span>
                  )} />
              )}
              <SRow label="Manage your data" lead={MDIS.drum(GLYPH(), 17)}
                accent={accent}
                onClick={() => goView('data')} />
              <StorageBarsS accent={accent} last />
            </SSection>

            {/* Account is intentionally last: identity and destructive server
                actions sit below device, prayer, and backup preferences rather
                than leading a screen primarily about how this device behaves. */}
            {(!auth || !auth.signedIn) && (
              <SSection label="Account">
                {/* WHILE THE ANSWER IS STILL COMING the section stands with
                    Theme and Language on it and nothing else — an unanswered
                    `auth` used to draw no Account section at all, which was
                    invisible while the section held only sign-in rows and
                    would now be two rows blinking into place on every open. */}
                {auth && (<>
                {/* No "create an account" here, and its absence is the rule:
                    a ManDana account is made once, at the front door, with the
                    username somebody already on ManDana wrote down. A button
                    here could only ever earn a refusal from the server. */}
                {/* PROSE INSIDE A CARD TAKES THE CARD'S PADDING. This sat on
                    a 4px margin, which is the measure for a paragraph BESIDE a
                    card — inside one it put the first line hard against the
                    rounded top edge, and on a phone the border ran through the
                    letters. Padded and ruled like a row, because that is what
                    it is standing in the place of. */}
                <p style={{ color: inkS(0.32), fontSize: 11.5, lineHeight: 1.6, margin: 0,
                  padding: '13px 16px 12px', borderBottom: `1px solid ${inkS(light ? 0.07 : 0.06)}` }}>
                  ManDana is by invitation. A new account is made at the front door, with the
                  username the person who invited you gave you.
                </p>
                {/* THE CAPTIONS CAME OFF THESE TWO LAST, and they were the
                    only two on the root the rule genuinely cost something:
                    "use Face ID or your device PIN" and "Enter a code from a
                    device already signed in" were instructions rather than
                    descriptions. The paragraph above the rows already says
                    ManDana is by invitation and where a code comes from, so
                    what is lost is the restatement, not the fact. */}
                {/* A KEY AND A PERSON, not a key and a plus. `MDI.plus` is
                    drawn for 12–13px — its 2.2 stroke at this column's size is
                    half again the weight of everything beside it — and the
                    second row is not really "add a thing" anyway: it is this
                    device joining somebody the account already knows. */}
                <SRow label="Sign in" lead={MDIS.key(GLYPH(), 17)}
                  right={busy === 'signin' ? '…' : null} accent={accent}
                  onClick={busy ? null : doSignIn} />
                <SRow label="Add this device to my account"
                  lead={MDIS.person(GLYPH(), 17)}
                  accent={accent} onClick={() => setLinking((v) => !v)} />
                </>)}
                {appearanceRows(true)}
              </SSection>
            )}

            {linking && !auth?.signedIn && (
              <div style={{ marginTop: 10 }}>
                <input value={linkInput} onChange={(e) => setLinkInput(e.target.value)}
                  placeholder="LINK-XXXX-XXXX-XXXX-XXXX" autoCapitalize="characters" spellCheck={false}
                  aria-label="The code from a device already signed in"
                  style={{ width: '100%', padding: '11px 14px', borderRadius: 12, fontSize: 15,
                    background: surfS(0.10), color: inkS(0.92), border: `1px solid ${inkS(light ? 0.10 : 0.08)}`,
                    outline: 'none', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }} />
                <button onClick={busy ? null : doUseLink} disabled={!!busy || !linkInput.trim()}
                  style={{ width: '100%', marginTop: 8, padding: 12, borderRadius: 12, border: 'none',
                    cursor: linkInput.trim() ? 'pointer' : 'default',
                    background: linkInput.trim() ? rgbaS(accent, 1) : inkS(0.10),
                    color: linkInput.trim() ? '#fff' : inkS(0.3), fontSize: 15, fontWeight: 650 }}>
                  {busy === 'link' ? 'Linking…' : 'Link this device'}
                </button>
              </div>
            )}

            {/* Three rows: who you are, how you get in, and the way out. The
                lists behind the first two are long, and a long list on the root
                is what this whole surface was. */}
            {auth && auth.signedIn && (
              <SSection label="Account">
                {person && (
                  /* THE FACE IS THE LEAD, and it is why the lead column is
                     24px rather than 17: the column is sized by its widest
                     member and the portrait is that member. Nothing stands at
                     the right of this row — the face is already the most
                     obvious door on the list. */
                  <SRow label={person.name || 'Your name'}
                    lead={<SFace url={face} initials={person.initials} size={S_LEAD} />}
                    accent={accent} onClick={() => goView('profile')} />
                )}
                <SRow label="Passkeys & devices" lead={MDIS.key(GLYPH(), 17)}
                  accent={accent}
                  onClick={() => goView('devices')} />
                {appearanceRows(false)}
                {/* Sign out stays here, at the bottom, where it always was. It
                    is the one thing on this list nobody should have to go
                    looking for. */}
                <SRow label="Sign out" lead={MDIS.signOut(GLYPH(), 17)}
                  right={busy === 'signout' ? '…' : null}
                  accent={accent} last
                  onClick={busy ? null : doSignOut} />
              </SSection>
            )}
          </>
        )}
      </div>
    </div>
    </>);
}

Object.assign(window, { mdTrashRowSay, mdTrashAskSay, mdSyncSaid, mdLocateSaid,
  mdCountryName, mdCountriesNamed, SettingsSheet });
